import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { 
  createChart, 
  CandlestickSeries, 
  HistogramSeries, 
  LineSeries, 
  ColorType, 
  CrosshairMode, 
  LineStyle,
  createSeriesMarkers 
} from "lightweight-charts";
import { 
  Eye, 
  EyeOff, 
  Maximize2, 
  Minimize2,
  FastForward, 
  Layers, 
  Settings, 
  X,
  Target,
  Clock,
  Lock,
  Bell
} from "lucide-react";
import ReplayToolbar from "./ReplayToolbar";
import DrawingToolbar from "./DrawingToolbar";
import DrawingActionBar from "./DrawingActionBar";
import UserProfileModal from "./UserProfileModal";
import FibonacciSettingsModal from "./FibonacciSettingsModal";
import IndicatorLibraryModal, { AVAILABLE_INDICATORS } from "./IndicatorLibraryModal";
import IndicatorSettingsModal from "./IndicatorSettingsModal";
import { useAuth } from "../contexts/AuthContext";
import { 
  getSavedTimezone, 
  getTimezoneLabel, 
  createChartTimeFormatter, 
  createChartTickMarkFormatter 
} from "../services/timezoneService";
import { calculateIchimoku, calculateMultiEMA, calculateMACD, calculateHMA } from "../services/indicatorEngine";
import { calculateSMC, calculateClassicalSMC } from "../services/smcEngine";
import { calculateHMASystem } from "../services/hmaSystemEngine";
import { calculateBobVolman } from "../services/bobVolmanEngine";
import { calculateOrbSessionData } from "../services/orbSessionEngine";
import { calculateLiquiditySweep } from "../services/liquiditySweepEngine";
import { calculateScalpingPro } from "../services/scalpingProEngine";
import { 
  loadUserDrawings, 
  saveUserDrawings, 
  clearUserDrawings, 
  getUserProfiles, 
  saveUserProfiles, 
  getActiveUserId, 
  setActiveUserId, 
  renderAllDrawings,
  loadSavedFibTemplate
} from "../services/drawingEngine";
import { findDrawingAtCoordinate, findDrawingHitTarget } from "../services/drawingHitTest";
import { api } from "../services/api";


const formatPrice = (val) => {
  if (val === null || val === undefined) return "";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  return num < 10 ? num.toFixed(5) : num.toFixed(2);
};

export default function LiveChart({
  bars,
  rawBars,
  indicators,
  signals,
  smc,
  hmaSystem,
  bobVolman,
  orbSession,
  liquiditySweep,
  scalpingPro,
  strategyMode,
  forecast,
  pineResult,
  currentSymbol,
  currentTimeframe,
  tick,
  lastWsBar,
  strategyParams,
  onApplyStrategy,
  publicConfig,
  currentTimezone = "local",
  onSelectTimezone,
  // Bar Replay Props
  isReplayMode,
  onStartReplay,
  onExitReplay,
  isPlaying,
  onTogglePlay,
  onStepForward,
  onResetReplay,
  replayIndex,
  totalBars,
  onSeek,
  replaySpeed,
  onChangeSpeed,
  // Cloud Workspace Sync Props
  onWorkspaceChange,
  syncStatus = "synced",
  restoredWorkspace,
  // Indicator Library Modal Props
  isLibraryOpen: externalLibraryOpen,
  onOpenLibrary: externalOpenLibrary,
  onCloseLibrary: externalCloseLibrary,
  activeIndicatorIds: propActiveIndicators,
  // Indicator Settings Modal Props
  settingsModalId: externalSettingsModalId,
  onOpenSettings: onExternalOpenSettings,
  onCloseSettings: onExternalCloseSettings,
  // Alert Props
  activeAlertsCount = 0,
  onOpenAlerts,
}) {
  const { isBasic, isAdmin, canAccess, promptTierGate } = useAuth();

  const mainWrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const overlayCanvasRef = useRef(null);

  // Matrix Strategy Series
  const ema9SeriesRef = useRef(null);
  const ema21SeriesRef = useRef(null);
  const ema50SeriesRef = useRef(null);
  const ema200SeriesRef = useRef(null);
  const bbUpperRef = useRef(null);
  const bbLowerRef = useRef(null);
  const markersRef = useRef(null);

  // Ichimoku Cloud Lines
  const ichiTenkanRef = useRef(null);
  const ichiKijunRef = useRef(null);
  const ichiChikouRef = useRef(null);
  const ichiSpanARef = useRef(null);
  const ichiSpanBRef = useRef(null);

  // Multi EMA Ribbon Series (Sonic R: 34, 89, 200, 610)
  const multiEma1Ref = useRef(null);
  const multiEma2Ref = useRef(null);
  const multiEma3Ref = useRef(null);
  const multiEma4Ref = useRef(null);

  // Bob Volman 20 EMA Native Series
  const bobVolmanEmaRef = useRef(null);

  // Price lines for TP/SL/Entry
  const entryLineRef = useRef(null);
  const tp1LineRef = useRef(null);
  const tp2LineRef = useRef(null);
  const slLineRef = useRef(null);

  // RSI Sub-chart refs
  const rsiContainerRef = useRef(null);
  const rsiChartRef = useRef(null);
  const rsiSeriesRef = useRef(null);

  const STORAGE_ACTIVE_INDICATORS = "minopro_active_indicators";
  const STORAGE_HIDDEN_INDICATORS = "minopro_hidden_indicators";
  const STORAGE_INDICATOR_STYLES = "minopro_indicator_styles";

  // Active Indicators (Persisted across sessions)
  const [activeIndicators, setActiveIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_ACTIVE_INDICATORS);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return ["hma_breakout_system"];
  });

  useEffect(() => {
    if (Array.isArray(propActiveIndicators)) {
      setActiveIndicators(propActiveIndicators);
    }
  }, [propActiveIndicators]);

  const [hiddenIndicators, setHiddenIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_HIDDEN_INDICATORS);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {}
    return [];
  });

  // Modals state
  const [internalLibraryOpen, setInternalLibraryOpen] = useState(false);
  const isLibraryOpen = externalLibraryOpen !== undefined ? externalLibraryOpen : internalLibraryOpen;
  const handleOpenLibrary = () => {
    if (externalOpenLibrary) externalOpenLibrary();
    else setInternalLibraryOpen(true);
  };
  const handleCloseLibrary = () => {
    if (externalCloseLibrary) externalCloseLibrary();
    else setInternalLibraryOpen(false);
  };

  const [internalSettingsModalId, setInternalSettingsModalId] = useState(null);
  const activeSettingsModalId = externalSettingsModalId !== undefined ? externalSettingsModalId : internalSettingsModalId;
  const handleOpenSettingsModal = (id) => {
    if (onExternalOpenSettings) onExternalOpenSettings(id);
    else setInternalSettingsModalId(id);
  };
  const handleCloseSettingsModal = () => {
    if (onExternalCloseSettings) onExternalCloseSettings();
    else setInternalSettingsModalId(null);
  };
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [fibModalDrawing, setFibModalDrawing] = useState(null);

  // User Profile & Drawing Tools State
  const [userProfiles, setUserProfiles] = useState(getUserProfiles());
  const [activeUserId, setActiveUserIdState] = useState(getActiveUserId());
  const [activeTool, setActiveTool] = useState("cursor");
  const [drawColor, setDrawColor] = useState("#38bdf8");
  const [drawings, setDrawings] = useState([]);
  const [activeDrawing, setActiveDrawing] = useState(null);
  const [selectedDrawingId, setSelectedDrawingId] = useState(null);
  const [actionBarPos, setActionBarPos] = useState({ x: 200, y: 100 });
  const isDrawingInProgressRef = useRef(false);

  // Drawings Ref for click handler
  const drawingsRef = useRef(drawings);
  useEffect(() => {
    drawingsRef.current = drawings;
  }, [drawings]);

  const activeToolRef = useRef(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  const selectedDrawingIdRef = useRef(selectedDrawingId);
  useEffect(() => {
    selectedDrawingIdRef.current = selectedDrawingId;
  }, [selectedDrawingId]);

  const dragStateRef = useRef(null);
  const isDraggingDrawingRef = useRef(false);
  const isPanningRef = useRef(false);

  const handleContainerMouseMove = (e) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (isDraggingDrawingRef.current || isDrawingInProgressRef.current) {
      canvas.style.pointerEvents = "auto";
      return;
    }

    if (activeTool !== "cursor") {
      canvas.style.pointerEvents = "auto";
      canvas.style.cursor = "crosshair";
      return;
    }

    const isOverRightScale = x >= rect.width - 75;
    const isOverBottomScale = y >= rect.height - 30;

    if (isOverRightScale || isOverBottomScale) {
      canvas.style.pointerEvents = "none";
      return;
    }

    const hit = findDrawingHitTarget(
      x,
      y,
      drawingsRef.current,
      selectedDrawingIdRef.current,
      chartRef.current,
      candleSeriesRef.current
    );

    if (hit.type === "handle" || hit.type === "body" || hit.type === "select") {
      canvas.style.pointerEvents = "auto";
      canvas.style.cursor = hit.cursor || "pointer";
    } else {
      canvas.style.pointerEvents = "none";
      canvas.style.cursor = "default";
    }
  };

  // Load drawings whenever activeUserId or currentSymbol changes
  useEffect(() => {
    const loaded = loadUserDrawings(activeUserId, currentSymbol);
    setDrawings(loaded);
    setSelectedDrawingId(null);
  }, [activeUserId, currentSymbol]);

  // Save drawings helper
  const handleSaveDrawings = (newDrawings) => {
    setDrawings(newDrawings);
    saveUserDrawings(activeUserId, currentSymbol, newDrawings);

    try {
      api.saveDrawings(activeUserId, currentSymbol, newDrawings).catch(() => {});
    } catch (e) {}

    if (onWorkspaceChange) {
      onWorkspaceChange({ drawings: newDrawings });
    }
  };

  const handleClearAllDrawings = () => {
    if (window.confirm(`Bạn có chắc chắn muốn xóa toàn bộ bản vẽ phân tích của cặp ${currentSymbol}?`)) {
      handleSaveDrawings([]);
      clearUserDrawings(activeUserId, currentSymbol);
      setSelectedDrawingId(null);
    }
  };

  const handleDeleteSelectedDrawing = (idToDelete) => {
    const targetId = idToDelete || selectedDrawingId;
    if (!targetId) return;
    const next = drawings.filter((d) => d.id !== targetId);
    handleSaveDrawings(next);
    setSelectedDrawingId(null);
  };

  const handleUpdateSelectedDrawing = (updated) => {
    const next = drawings.map((d) => (d.id === updated.id ? updated : d));
    handleSaveDrawings(next);
  };

  const handleDuplicateDrawing = (item) => {
    if (!item) return;
    const clone = JSON.parse(JSON.stringify(item));
    clone.id = `draw_${Date.now()}`;
    if (clone.p1 && clone.p2) {
      clone.p1.price += 1.0;
      clone.p2.price += 1.0;
    } else if (clone.price) {
      clone.price += 1.0;
    }
    const next = [...drawings, clone];
    handleSaveDrawings(next);
    setSelectedDrawingId(clone.id);
  };

  // Keyboard Delete / Backspace key listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selectedDrawingId) {
        if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
        handleDeleteSelectedDrawing(selectedDrawingId);
      } else if (e.key === "Escape") {
        setSelectedDrawingId(null);
        setActiveTool("cursor");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedDrawingId, drawings]);

  const handleSelectUser = (id) => {
    setActiveUserId(id);
    setActiveUserIdState(id);
    const loaded = loadUserDrawings(id, currentSymbol);
    setDrawings(loaded);
    setSelectedDrawingId(null);
  };

  const handleCreateProfile = (profile) => {
    const next = [...userProfiles, profile];
    setUserProfiles(next);
    saveUserProfiles(next);
    handleSelectUser(profile.id);
  };

  const handleDeleteProfile = (id) => {
    const next = userProfiles.filter((p) => p.id !== id);
    setUserProfiles(next);
    saveUserProfiles(next);
    if (activeUserId === id && next.length > 0) {
      handleSelectUser(next[0].id);
    }
  };

  // Customizable Indicator Styles
  const DEFAULT_INDICATOR_STYLES = {
    hma: {
      visible: true,
      period: 21,
      bullColor: "#10b981",
      bearColor: "#ef4444",
      width: 2,
      style: 0,
    },
    ichimoku: {
      tenkan: { visible: true, color: "#06b6d4", width: 1, style: 0 },
      kijun: { visible: true, color: "#f59e0b", width: 2, style: 0 },
      chikou: { visible: true, color: "#10b981", width: 1, style: 1 },
      spanA: { visible: true, color: "#10b981", width: 1, style: 0 },
      spanB: { visible: true, color: "#ef4444", width: 1, style: 0 },
      cloudFill: true,
      cloudBullColor: "#10b981",
      cloudBearColor: "#ef4444",
    },
    multiEma: {
      ema1: { visible: true, color: "#38bdf8", width: 1, style: 0 },
      ema2: { visible: true, color: "#facc15", width: 2, style: 0 },
      ema3: { visible: true, color: "#c084fc", width: 2, style: 0 },
      ema4: { visible: true, color: "#fb7185", width: 2, style: 0 },
    },
    bb: {
      upper: { visible: true, color: "#38bdf8", width: 1, style: 2 },
      lower: { visible: true, color: "#38bdf8", width: 1, style: 2 },
    },
    smc: {
      anchor: { visible: false, color: "#ef4444", width: 2, style: 0 },
      mid: { visible: false, color: "#38bdf8", width: 1, style: 0 },
      same: { visible: false, color: "#c084fc", width: 1, style: 0 },
      entry: { visible: true, color: "#facc15", width: 1, style: 2 }, // amber dashed = limit line
      tp1: { visible: true, color: "#84cc16", width: 1, style: 0 },
      tp2: { visible: true, color: "#22c55e", width: 1, style: 0 },
      sl: { visible: true, color: "#f97316", width: 1, style: 0 },
      showLabels: true,
    },
    hmaSystem: {
      hmaLine: { visible: true, bullColor: "#10b981", bearColor: "#ef4444", width: 2, style: 0 },
      buyBoxColor: "#06b6d4",
      sellBoxColor: "#f59e0b",
      entry: { visible: true, color: "#facc15", width: 1, style: 2 },
      tp1: { visible: true, color: "#84cc16", width: 1, style: 0 },
      tp2: { visible: true, color: "#22c55e", width: 1, style: 0 },
      sl: { visible: true, color: "#ef4444", width: 1, style: 0 },
      showLabels: true,
    },
    bobVolman: {
      emaLine: { visible: true, color: "#06b6d4", width: 2, style: 0 },
      barrierColor: "#f59e0b",
      trendlineColor: "#c084fc",
      buyBuildupColor: "#06b6d4",
      sellBuildupColor: "#f43f5e",
      entry: { visible: true, color: "#38bdf8", width: 1.5, style: 0 },
      tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
      tp2: { visible: true, color: "#059669", width: 1.5, style: 2 },
      sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
      showLabels: true,
    },
    orbSession: {
      asiaBoxColor: "#38bdf8",
      londonBoxColor: "#eab308",
      nyBoxColor: "#a855f7",
      entry: { visible: true, color: "#38bdf8", width: 1.5, style: 0 },
      tp: { visible: true, color: "#10b981", width: 1.5, style: 2 },
      sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
      showLabels: true,
    },
    scalpingPro: {
      bslColor: "#f59e0b",
      sslColor: "#06b6d4",
      mssColor: "#a855f7",
      entry: { visible: true, color: "#8b5cf6", width: 1.5, style: 0 },
      tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
      tp2: { visible: true, color: "#059669", width: 1.5, style: 2 },
      sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
      showLabels: true,
      showZones: false,
    },
    liquiditySweep: {
      bslColor: "#f59e0b",
      sslColor: "#06b6d4",
      mssColor: "#a855f7",
      entry: { visible: true, color: "#8b5cf6", width: 1.5, style: 0 },
      tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
      tp2: { visible: true, color: "#059669", width: 1.5, style: 2 },
      sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
      showLabels: true,
      showZones: false,
    },
  };

  const [indicatorStyles, setIndicatorStyles] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_INDICATOR_STYLES);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...DEFAULT_INDICATOR_STYLES,
          ...parsed,
          hma: { ...DEFAULT_INDICATOR_STYLES.hma, ...(parsed.hma || {}) },
          hmaSystem: { ...DEFAULT_INDICATOR_STYLES.hmaSystem, ...(parsed.hmaSystem || {}) },
          bobVolman: { ...DEFAULT_INDICATOR_STYLES.bobVolman, ...(parsed.bobVolman || {}) },
          orbSession: { ...DEFAULT_INDICATOR_STYLES.orbSession, ...(parsed.orbSession || {}) },
          scalpingPro: {
      bslColor: "#f59e0b",
      sslColor: "#06b6d4",
      mssColor: "#a855f7",
      entry: { visible: true, color: "#8b5cf6", width: 1.5, style: 0 },
      tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
      tp2: { visible: true, color: "#059669", width: 1.5, style: 2 },
      sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
      showLabels: true,
      showZones: false,
    },
    scalpingPro: { ...DEFAULT_INDICATOR_STYLES.scalpingPro, ...(parsed.scalpingPro || {}) },
          liquiditySweep: { ...DEFAULT_INDICATOR_STYLES.liquiditySweep, ...(parsed.liquiditySweep || {}) },
          smc: { ...DEFAULT_INDICATOR_STYLES.smc, ...(parsed.smc || {}) },
          ichimoku: { ...DEFAULT_INDICATOR_STYLES.ichimoku, ...(parsed.ichimoku || {}) },
          multiEma: { ...DEFAULT_INDICATOR_STYLES.multiEma, ...(parsed.multiEma || {}) },
          bb: { ...DEFAULT_INDICATOR_STYLES.bb, ...(parsed.bb || {}) },
        };
      }
    } catch (e) {}
    return DEFAULT_INDICATOR_STYLES;
  });

  // When cloud workspace is restored from server
  useEffect(() => {
    if (restoredWorkspace) {
      if (Array.isArray(restoredWorkspace.activeIndicators) && restoredWorkspace.activeIndicators.length > 0) {
        setActiveIndicators(restoredWorkspace.activeIndicators);
      }
      if (Array.isArray(restoredWorkspace.hiddenIndicators)) {
        setHiddenIndicators(restoredWorkspace.hiddenIndicators);
      }
      if (restoredWorkspace.indicatorStyles && typeof restoredWorkspace.indicatorStyles === "object") {
        setIndicatorStyles((prev) => ({
          ...DEFAULT_INDICATOR_STYLES,
          ...prev,
          ...restoredWorkspace.indicatorStyles,
        }));
      }
      if (Array.isArray(restoredWorkspace.drawings)) {
        setDrawings(restoredWorkspace.drawings);
      }
    }
  }, [restoredWorkspace]);

  const handleApplyStyles = (newStyles) => {
    setIndicatorStyles((prev) => {
      const updated = { ...prev, ...newStyles };
      try {
        localStorage.setItem(STORAGE_INDICATOR_STYLES, JSON.stringify(updated));
      } catch (e) {}
      if (onWorkspaceChange) {
        onWorkspaceChange({ indicatorStyles: updated });
      }
      return updated;
    });
  };

  // Selecting Bar State (TradingView Scissors Mode)
  const [isSelectingBar, setIsSelectingBar] = useState(false);
  const isSelectingBarRef = useRef(isSelectingBar);
  const rawBarsRef = useRef(rawBars || []);
  const targetSeekLogicalRef = useRef(null);
  const onSeekRef = useRef(onSeek);

  useEffect(() => {
    isSelectingBarRef.current = isSelectingBar;
  }, [isSelectingBar]);

  useEffect(() => {
    rawBarsRef.current = rawBars || [];
  }, [rawBars]);

  useEffect(() => {
    onSeekRef.current = onSeek;
  }, [onSeek]);

  useEffect(() => {
    setIsSelectingBar(false);
  }, [isReplayMode]);

  const getPrecision = (sym) => {
    if (sym === "EURUSD" || sym === "GBPUSD") return { precision: 5, minMove: 0.00001 };
    if (sym === "USDJPY") return { precision: 3, minMove: 0.001 };
    return { precision: 2, minMove: 0.01 };
  };

  const handleToggleIndicator = (id) => {
    const itemDef = AVAILABLE_INDICATORS.find((i) => i.id === id);
    if (itemDef?.isSystem && !isBasic) {
      promptTierGate(itemDef.name || "Hệ Thống Giao Dịch", "BASIC");
      return;
    }
    let next;
    let nextHidden = hiddenIndicators;
    let newStrategyMode = undefined;
    if (activeIndicators.includes(id)) {
      next = activeIndicators.filter((item) => item !== id);
      nextHidden = hiddenIndicators.filter((item) => item !== id);
      setHiddenIndicators(nextHidden);
      try {
        localStorage.setItem(STORAGE_HIDDEN_INDICATORS, JSON.stringify(nextHidden));
      } catch (e) {}
    } else {
      next = [...activeIndicators, id];
      if (itemDef?.isSystem) {
        const sysModeMap = {
          msb_dynamic: "smc",
          hma_breakout_system: "hma",
          bob_volman_system: "bob_volman",
          orb_session_system: "orb_session",
          matrix_pro: "matrix",
          liquidity_sweep_pro: "liquidity_sweep",
          scalping_pro_system: "scalping_pro",
        };
        if (id === "liquidity_sweep_pro") {
          let adminCfg = {};
          if (publicConfig && typeof publicConfig === "object" && Object.keys(publicConfig).length > 0) {
            adminCfg = publicConfig;
          } else {
            try {
              const saved = localStorage.getItem("minopro_public_config");
              if (saved) adminCfg = JSON.parse(saved);
            } catch (e) {}
          }
          const isSweepCustomized = Boolean(strategyParams?.userCustomizedSweep);
          if (!isSweepCustomized) {
            const sweepDefaults = {
              sweepM1AnchorM5: adminCfg.sweep_m1_anchor_m5 !== undefined ? Boolean(adminCfg.sweep_m1_anchor_m5) : true,
              sweep_m1_anchor_m5: adminCfg.sweep_m1_anchor_m5 !== undefined ? Boolean(adminCfg.sweep_m1_anchor_m5) : true,
              sweepLookback: adminCfg.sweep_lookback !== undefined ? Number(adminCfg.sweep_lookback) : 5,
              sweep_lookback: adminCfg.sweep_lookback !== undefined ? Number(adminCfg.sweep_lookback) : 5,
              len: adminCfg.sweep_lookback !== undefined ? Number(adminCfg.sweep_lookback) : 5,
              sweepOpt: "Only Outbreaks & Retest",
              sweep_opt: "Only Outbreaks & Retest",
              opt: "Only Outbreaks & Retest",
              sweepExtend: adminCfg.sweep_extend !== undefined ? Boolean(adminCfg.sweep_extend) : true,
              sweep_extend: adminCfg.sweep_extend !== undefined ? Boolean(adminCfg.sweep_extend) : true,
              sweepMaxBars: adminCfg.sweep_max_bars !== undefined ? Number(adminCfg.sweep_max_bars) : 300,
              sweep_max_bars: adminCfg.sweep_max_bars !== undefined ? Number(adminCfg.sweep_max_bars) : 300,
              sweepEntryMode: adminCfg.sweep_entry_mode || "SMART_HYBRID",
              sweep_entry_mode: adminCfg.sweep_entry_mode || "SMART_HYBRID",
              entry_mode: adminCfg.sweep_entry_mode || "SMART_HYBRID",
              sweepRr: adminCfg.sweep_rr !== undefined ? Number(adminCfg.sweep_rr) : 2.0,
              sweep_rr: adminCfg.sweep_rr !== undefined ? Number(adminCfg.sweep_rr) : 2.0,
              sweepSlBufferPips: adminCfg.sweep_sl_buffer_pips !== undefined ? Number(adminCfg.sweep_sl_buffer_pips) : 1.5,
              sweep_sl_buffer_pips: adminCfg.sweep_sl_buffer_pips !== undefined ? Number(adminCfg.sweep_sl_buffer_pips) : 1.5,
              sweepMinRiskPips: adminCfg.sweep_min_risk_pips !== undefined ? Number(adminCfg.sweep_min_risk_pips) : 3.0,
              sweep_min_risk_pips: adminCfg.sweep_min_risk_pips !== undefined ? Number(adminCfg.sweep_min_risk_pips) : 3.0,
              sweepMaxRiskPips: adminCfg.sweep_max_risk_pips !== undefined ? Number(adminCfg.sweep_max_risk_pips) : 150.0,
              sweep_max_risk_pips: adminCfg.sweep_max_risk_pips !== undefined ? Number(adminCfg.sweep_max_risk_pips) : 150.0,
              sweepCooldownBars: adminCfg.sweep_cooldown_bars !== undefined ? Number(adminCfg.sweep_cooldown_bars) : 3,
              sweep_cooldown_bars: adminCfg.sweep_cooldown_bars !== undefined ? Number(adminCfg.sweep_cooldown_bars) : 3,
              sweepMinSwingPips: adminCfg.sweep_min_swing_pips !== undefined ? Number(adminCfg.sweep_min_swing_pips) : 25.0,
              sweep_min_swing_pips: adminCfg.sweep_min_swing_pips !== undefined ? Number(adminCfg.sweep_min_swing_pips) : 25.0,
              sweepCompareBars: adminCfg.sweep_compare_bars !== undefined ? Number(adminCfg.sweep_compare_bars) : 25,
              sweep_compare_bars: adminCfg.sweep_compare_bars !== undefined ? Number(adminCfg.sweep_compare_bars) : 25,
              sweepHighestPeakFilter: adminCfg.sweep_highest_peak_filter !== undefined ? Boolean(adminCfg.sweep_highest_peak_filter) : false,
              sweep_highest_peak_filter: adminCfg.sweep_highest_peak_filter !== undefined ? Boolean(adminCfg.sweep_highest_peak_filter) : false,
              sweepPeakLookback: adminCfg.sweep_peak_lookback !== undefined ? Number(adminCfg.sweep_peak_lookback) : 30,
              sweep_peak_lookback: adminCfg.sweep_peak_lookback !== undefined ? Number(adminCfg.sweep_peak_lookback) : 30,
              sweepMaxWaitBars: adminCfg.sweep_max_wait_bars !== undefined ? Number(adminCfg.sweep_max_wait_bars) : 5,
              sweep_max_wait_bars: adminCfg.sweep_max_wait_bars !== undefined ? Number(adminCfg.sweep_max_wait_bars) : 5,
              sweepTrendFilterEnable: adminCfg.sweep_trend_filter_enable !== undefined ? Boolean(adminCfg.sweep_trend_filter_enable) : false,
              sweep_trend_filter_enable: adminCfg.sweep_trend_filter_enable !== undefined ? Boolean(adminCfg.sweep_trend_filter_enable) : false,
              sweepTrendEmaPeriod: adminCfg.sweep_trend_ema_period !== undefined ? Number(adminCfg.sweep_trend_ema_period) : 200,
              sweep_trend_ema_period: adminCfg.sweep_trend_ema_period !== undefined ? Number(adminCfg.sweep_trend_ema_period) : 200,
              sweepSessionFilterEnable: adminCfg.sweep_session_filter_enable !== undefined ? Boolean(adminCfg.sweep_session_filter_enable) : false,
              sweep_session_filter_enable: adminCfg.sweep_session_filter_enable !== undefined ? Boolean(adminCfg.sweep_session_filter_enable) : false,
              sweepSessionStart: adminCfg.sweep_session_start || "00:00",
              sweep_session_start: adminCfg.sweep_session_start || "00:00",
              sweepSessionEnd: adminCfg.sweep_session_end || "23:59",
              sweep_session_end: adminCfg.sweep_session_end || "23:59",
              sweepLimitCancelBars: adminCfg.sweep_limit_cancel_bars !== undefined ? Number(adminCfg.sweep_limit_cancel_bars) : 30,
              sweep_limit_cancel_bars: adminCfg.sweep_limit_cancel_bars !== undefined ? Number(adminCfg.sweep_limit_cancel_bars) : 30,
              sweepCloseOnOpposite: adminCfg.sweep_close_on_opposite !== undefined ? Boolean(adminCfg.sweep_close_on_opposite) : false,
              sweep_close_on_opposite: adminCfg.sweep_close_on_opposite !== undefined ? Boolean(adminCfg.sweep_close_on_opposite) : false,
              sweepDynamicBeEnable: adminCfg.sweep_dynamic_be_enable !== undefined ? Boolean(adminCfg.sweep_dynamic_be_enable) : false,
              sweep_dynamic_be_enable: adminCfg.sweep_dynamic_be_enable !== undefined ? Boolean(adminCfg.sweep_dynamic_be_enable) : false,
              sweepBeTriggerMode: adminCfg.sweep_be_trigger_mode || "PCT_TP",
              sweep_be_trigger_mode: adminCfg.sweep_be_trigger_mode || "PCT_TP",
              sweepBeTriggerPct: adminCfg.sweep_be_trigger_pct !== undefined ? Number(adminCfg.sweep_be_trigger_pct) : 50.0,
              sweep_be_trigger_pct: adminCfg.sweep_be_trigger_pct !== undefined ? Number(adminCfg.sweep_be_trigger_pct) : 50.0,
              sweepBeTriggerPips: adminCfg.sweep_be_trigger_pips !== undefined ? Number(adminCfg.sweep_be_trigger_pips) : 40.0,
              sweep_be_trigger_pips: adminCfg.sweep_be_trigger_pips !== undefined ? Number(adminCfg.sweep_be_trigger_pips) : 40.0,
              sweepBeLockPips: adminCfg.sweep_be_lock_pips !== undefined ? Number(adminCfg.sweep_be_lock_pips) : 2.0,
              sweep_be_lock_pips: adminCfg.sweep_be_lock_pips !== undefined ? Number(adminCfg.sweep_be_lock_pips) : 2.0,
              sweepHybridThresholdPips: adminCfg.sweep_hybrid_threshold_pips !== undefined ? Number(adminCfg.sweep_hybrid_threshold_pips) : 150.0,
              sweep_hybrid_threshold_pips: adminCfg.sweep_hybrid_threshold_pips !== undefined ? Number(adminCfg.sweep_hybrid_threshold_pips) : 150.0,
              sweepHybridLimitMode: adminCfg.sweep_hybrid_limit_mode || "ZONE_50",
              sweep_hybrid_limit_mode: adminCfg.sweep_hybrid_limit_mode || "ZONE_50",
              sweepHybridZonePct: adminCfg.sweep_hybrid_zone_pct !== undefined ? Number(adminCfg.sweep_hybrid_zone_pct) : 50.0,
              sweep_hybrid_zone_pct: adminCfg.sweep_hybrid_zone_pct !== undefined ? Number(adminCfg.sweep_hybrid_zone_pct) : 50.0,
              sweepHybridPullbackPct: adminCfg.sweep_hybrid_pullback_pct !== undefined ? Number(adminCfg.sweep_hybrid_pullback_pct) : 50.0,
              sweep_hybrid_pullback_pct: adminCfg.sweep_hybrid_pullback_pct !== undefined ? Number(adminCfg.sweep_hybrid_pullback_pct) : 50.0,
            };
            if (onApplyStrategy) {
              onApplyStrategy(sweepDefaults);
            }
          }
        }
        if (sysModeMap[id]) {
          newStrategyMode = sysModeMap[id];
          try {
            localStorage.setItem("minopro_signal_strategy_mode", newStrategyMode);
          } catch (e) {}
        }
      }
    }
    setActiveIndicators(next);
    try {
      localStorage.setItem(STORAGE_ACTIVE_INDICATORS, JSON.stringify(next));
    } catch (e) {}
    if (onWorkspaceChange) {
      const payload = { activeIndicators: next, hiddenIndicators: nextHidden };
      if (newStrategyMode) payload.strategyMode = newStrategyMode;
      onWorkspaceChange(payload);
    }
  };

  const handleToggleHide = (id) => {
    let next;
    if (hiddenIndicators.includes(id)) {
      next = hiddenIndicators.filter((item) => item !== id);
    } else {
      next = [...hiddenIndicators, id];
    }
    setHiddenIndicators(next);
    try {
      localStorage.setItem(STORAGE_HIDDEN_INDICATORS, JSON.stringify(next));
    } catch (e) {}
    if (onWorkspaceChange) {
      onWorkspaceChange({ hiddenIndicators: next });
    }
  };

  const isMatrixProActive = Boolean(
    isBasic &&
    activeIndicators.includes("matrix_pro") &&
    strategyMode === "matrix" &&
    !hiddenIndicators.includes("matrix_pro")
  );
  const isClassicalSMCActive = Boolean(
    activeIndicators.includes("smc_structure") &&
    !hiddenIndicators.includes("smc_structure")
  );
  const isMSBDynamicActive = Boolean(
    isBasic &&
    activeIndicators.includes("msb_dynamic") &&
    strategyMode === "smc" &&
    !hiddenIndicators.includes("msb_dynamic")
  );
  const isSMCActive = isClassicalSMCActive || isMSBDynamicActive;
  const isBBActive = activeIndicators.includes("bollinger_bands") && !hiddenIndicators.includes("bollinger_bands");
  const isIchimokuActive = activeIndicators.includes("ichimoku_cloud") && !hiddenIndicators.includes("ichimoku_cloud");
  const isMultiEmaActive = activeIndicators.includes("multi_ema_ribbon") && !hiddenIndicators.includes("multi_ema_ribbon");
  const isHMAActive = activeIndicators.includes("hma_indicator") && !hiddenIndicators.includes("hma_indicator");
  const isHMABreakoutActive = Boolean(
    isBasic &&
    activeIndicators.includes("hma_breakout_system") &&
    strategyMode === "hma" &&
    !hiddenIndicators.includes("hma_breakout_system")
  );
  const isBobVolmanActive = Boolean(
    isBasic &&
    activeIndicators.includes("bob_volman_system") &&
    strategyMode === "bob_volman" &&
    !hiddenIndicators.includes("bob_volman_system")
  );
  const isOrbSessionActive = Boolean(
    isAdmin &&
    isBasic &&
    activeIndicators.includes("orb_session_system") &&
    strategyMode === "orb_session" &&
    !hiddenIndicators.includes("orb_session_system")
  );
    const isScalpingProActive = Boolean(
    isBasic &&
    activeIndicators.includes("scalping_pro_system") &&
    strategyMode === "scalping_pro" &&
    !hiddenIndicators.includes("scalping_pro_system")
  );

  const isLiquiditySweepActive = Boolean(
    isBasic &&
    activeIndicators.includes("liquidity_sweep_pro") &&
    strategyMode === "liquidity_sweep" &&
    !hiddenIndicators.includes("liquidity_sweep_pro")
  );

  const displayedIndicators = useMemo(() => {
    const currentSystemId =
      strategyMode === "scalping_pro" ? "scalping_pro_system" :
      strategyMode === "liquidity_sweep" ? "liquidity_sweep_pro" :
      strategyMode === "orb_session" ? (isAdmin ? "orb_session_system" : null) :
      strategyMode === "bob_volman" ? "bob_volman_system" :
      strategyMode === "hma" ? "hma_breakout_system" :
      strategyMode === "matrix" ? "matrix_pro" :
      "msb_dynamic";

    const SYSTEM_IDS = ["matrix_pro", "msb_dynamic", "hma_breakout_system", "bob_volman_system", "orb_session_system", "liquidity_sweep_pro", "scalping_pro_system"];
    const technicals = activeIndicators.filter((id) => !SYSTEM_IDS.includes(id));
    
    // Only display the single active trading system matching strategyMode + any technical indicators
    const hasCurrentSystem = currentSystemId && activeIndicators.includes(currentSystemId) && (currentSystemId !== "orb_session_system" || isAdmin);
    return hasCurrentSystem ? [currentSystemId, ...technicals] : technicals;
  }, [strategyMode, activeIndicators, isAdmin]);

  const showEMA = isMatrixProActive && strategyParams?.showEMA === true;
  const showTPSL = isMatrixProActive && strategyParams?.showTPSL !== false;
  const showRSI = isMatrixProActive && strategyParams?.showRSI === true;

  const hmaPeriod = indicatorStyles.hma?.period || 21;
  const hmaData = useMemo(() => {
    if (!isHMAActive || !bars || bars.length === 0) return null;
    const calcBars = bars;
    return calculateHMA(calcBars, { period: hmaPeriod });
  }, [bars, isHMAActive, hmaPeriod]);

  const hmaSystemData = useMemo(() => {
    if (!isHMABreakoutActive) return null;
    if (hmaSystem) return hmaSystem;
    if (!bars || bars.length === 0) return null;
    const calcBars = bars;
    return calculateHMASystem(calcBars, { ...strategyParams, timeframe: currentTimeframe });
  }, [bars, isHMABreakoutActive, hmaSystem, strategyParams, currentTimeframe]);

  const bobVolmanData = useMemo(() => {
    if (!isBobVolmanActive) return null;
    if (bobVolman) return bobVolman;
    if (!bars || bars.length === 0) return null;
    const calcBars = bars;
    return calculateBobVolman(calcBars, strategyParams);
  }, [bars, isBobVolmanActive, bobVolman, strategyParams]);

  const orbSessionData = useMemo(() => {
    if (!isOrbSessionActive) return null;
    if (orbSession && !isReplayMode) return orbSession;
    if (!bars || bars.length === 0) return null;
    return calculateOrbSessionData(bars, strategyParams);
  }, [bars, isOrbSessionActive, orbSession, strategyParams, isReplayMode]);

  const scalpingProData = useMemo(() => {
    if (!isScalpingProActive) return null;
    if (scalpingPro) return scalpingPro;
    if (!bars || bars.length === 0) return null;
    return calculateScalpingPro(bars, strategyParams);
  }, [bars, isScalpingProActive, scalpingPro, strategyParams]);

    const liquiditySweepData = useMemo(() => {
    if (!isLiquiditySweepActive) return null;
    if (liquiditySweep) return liquiditySweep;
    if (!bars || bars.length === 0) return null;
    return calculateLiquiditySweep(bars, strategyParams);
  }, [bars, isLiquiditySweepActive, liquiditySweep, strategyParams]);

  const ichimokuData = useMemo(() => {
    if (!isIchimokuActive || !bars || bars.length === 0) return null;
    return calculateIchimoku(bars);
  }, [bars, isIchimokuActive]);

  const multiEmaData = useMemo(() => {
    if (!isMultiEmaActive || !bars || bars.length === 0) return null;
    return calculateMultiEMA(bars);
  }, [bars, isMultiEmaActive]);

  const classicalSMCData = useMemo(() => {
    if (!isClassicalSMCActive || !bars || bars.length === 0) return null;
    const calcBars = bars;
    return calculateClassicalSMC(calcBars, strategyParams);
  }, [bars, isClassicalSMCActive, strategyParams]);

  const smcData = useMemo(() => {
    if (!isMSBDynamicActive || !bars || bars.length === 0) return null;
    if (smc) return smc;
    const calcBars = bars;
    return calculateSMC(calcBars, strategyParams);
  }, [bars, isMSBDynamicActive, smc, strategyParams]);

  // Fast Binary Search for Visible Timestamp Indices in Sorted Indicator Arrays (Spatial Culling)
  const findVisibleTimeRangeIndices = (points, fromTime, toTime) => {
    if (!points || points.length === 0) return { start: 0, end: 0 };
    if (fromTime === undefined || toTime === undefined || fromTime === null || toTime === null) {
      return { start: 0, end: points.length - 1 };
    }
    let low = 0;
    let high = points.length - 1;
    let start = 0;
    while (low <= high) {
      const mid = (low + high) >> 1;
      if (points[mid].time < fromTime) {
        start = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    start = Math.max(0, start - 2);

    low = start;
    high = points.length - 1;
    let end = points.length - 1;
    while (low <= high) {
      const mid = (low + high) >> 1;
      if (points[mid].time > toTime) {
        end = mid;
        high = mid - 1;
      } else {
        low = mid + 1;
      }
    }
    end = Math.min(points.length - 1, end + 2);
    return { start, end };
  };

  // Master Canvas Overlay Renderer
  const renderCanvasOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    const chart = chartRef.current;
    const candleSeries = candleSeriesRef.current;
    if (!canvas || !chart || !candleSeries) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    try {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const hexToRgba = (hex, alpha) => {
        let r = 16, g = 185, b = 129;
        if (hex && hex.startsWith("#") && hex.length === 7) {
          r = parseInt(hex.slice(1, 3), 16);
          g = parseInt(hex.slice(3, 5), 16);
          b = parseInt(hex.slice(5, 7), 16);
        }
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const timeScale = chart.timeScale();
      const visibleRange = timeScale.getVisibleRange();

    // 1. Draw Ichimoku Kumo Cloud (Spatially Culled to Visible Window)
    if (isIchimokuActive && ichimokuData && ichimokuData.cloudData && ichimokuData.cloudData.length >= 2) {
      const cloud = ichimokuData.cloudData;
      const ichiStyle = indicatorStyles.ichimoku;

      if (ichiStyle?.cloudFill !== false) {
        const bullCol = ichiStyle?.cloudBullColor || "#10b981";
        const bearCol = ichiStyle?.cloudBearColor || "#ef4444";

        const bullFill = hexToRgba(bullCol, 0.18);
        const bearFill = hexToRgba(bearCol, 0.18);

        const { start, end } = visibleRange
          ? findVisibleTimeRangeIndices(cloud, visibleRange.from, visibleRange.to)
          : { start: 0, end: cloud.length - 1 };

        for (let i = start; i < end; i++) {
          const p1 = cloud[i];
          const p2 = cloud[i + 1];

          const x1 = timeScale.timeToCoordinate(p1.time);
          const x2 = timeScale.timeToCoordinate(p2.time);
          if (x1 === null || x2 === null) continue;

          const yA1 = candleSeries.priceToCoordinate(p1.spanA);
          const yB1 = candleSeries.priceToCoordinate(p1.spanB);
          const yA2 = candleSeries.priceToCoordinate(p2.spanA);
          const yB2 = candleSeries.priceToCoordinate(p2.spanB);
          if (yA1 === null || yB1 === null || yA2 === null || yB2 === null) continue;

          const isBull = p1.spanA >= p1.spanB;

          ctx.beginPath();
          ctx.moveTo(x1, yA1);
          ctx.lineTo(x2, yA2);
          ctx.lineTo(x2, yB2);
          ctx.lineTo(x1, yB1);
          ctx.closePath();

          ctx.fillStyle = isBull ? bullFill : bearFill;
          ctx.fill();
        }
      }
    }

    // 2. Draw Classical SMC Indicator (Order Blocks, FVG, BOS / CHoCH)
    if (isClassicalSMCActive && classicalSMCData) {
      const classicalSt = indicatorStyles.classicalSmc || {};
      const bullColor = classicalSt.bullishOBColor || "#10b981";
      const bearColor = classicalSt.bearishOBColor || "#ef4444";
      const bullFvgCol = classicalSt.bullishFVGColor || "#06b6d4";
      const bearFvgCol = classicalSt.bearishFVGColor || "#f43f5e";
      const bosCol = classicalSt.bosColor || "#38bdf8";
      const chochCol = classicalSt.chochColor || "#f59e0b";
      const showLabels = classicalSt.showLabels !== false;
      const obFillOpacity = 0.18;
      const fvgFillOpacity = 0.14;

      // 2.1 Order Blocks (+OB Bullish / -OB Bearish) - Off-screen Culled
      if (strategyParams?.showOB !== false) {
        const obs = classicalSMCData.orderBlocks || [];
        obs.forEach((ob) => {
          if (strategyParams?.hideMitigatedOB && ob.is_mitigated) return;
          const sTime = ob.startTime;
          const eTime = ob.endTime || (bars && bars[bars.length - 1]?.time);
          if (visibleRange) {
            if (eTime && eTime < visibleRange.from) return;
            if (sTime && sTime > visibleRange.to) return;
          }
          const isBull = ob.type === "BULLISH_OB";
          const col = isBull ? bullColor : bearColor;

          const x1 = timeScale.timeToCoordinate(sTime);
          let x2 = timeScale.timeToCoordinate(eTime);
          if (x1 === null) return;
          if (x2 === null || x2 < x1) x2 = canvas.width - 60;

          const yTop = candleSeries.priceToCoordinate(ob.top);
          const yBottom = candleSeries.priceToCoordinate(ob.bottom);
          if (yTop === null || yBottom === null) return;

          const boxY = Math.min(yTop, yBottom);
          const boxH = Math.max(Math.abs(yBottom - yTop), 4);
          const boxW = Math.max(x2 - x1, 20);

          ctx.save();
          ctx.fillStyle = hexToRgba(col, ob.is_mitigated ? obFillOpacity * 0.45 : obFillOpacity);
          ctx.fillRect(x1, boxY, boxW, boxH);

          ctx.strokeStyle = hexToRgba(col, ob.is_mitigated ? 0.35 : 0.8);
          ctx.lineWidth = 1;
          ctx.setLineDash(ob.is_mitigated ? [3, 3] : []);
          ctx.strokeRect(x1, boxY, boxW, boxH);

          if (showLabels) {
            const labelText = isBull ? "+OB" : "-OB";
            ctx.font = "bold 9px monospace";
            const textW = ctx.measureText(labelText).width;
            ctx.fillStyle = hexToRgba(col, 0.4);
            ctx.fillRect(x1 + 3, boxY + 2, textW + 6, 12);
            ctx.fillStyle = "#ffffff";
            ctx.fillText(labelText, x1 + 6, boxY + 11);
          }
          ctx.restore();
        });
      }

      // 2.2 Fair Value Gaps (FVG) - Off-screen Culled
      if (strategyParams?.showFVG !== false) {
        const fvgs = classicalSMCData.fvgZones || [];
        fvgs.forEach((fvg) => {
          if (strategyParams?.hideMitigatedFVG && fvg.is_mitigated) return;
          const sTime = fvg.startTime;
          const eTime = fvg.endTime || (bars && bars[bars.length - 1]?.time);
          if (visibleRange) {
            if (eTime && eTime < visibleRange.from) return;
            if (sTime && sTime > visibleRange.to) return;
          }
          const isBull = fvg.type === "BULLISH_FVG";
          const col = isBull ? bullFvgCol : bearFvgCol;

          const x1 = timeScale.timeToCoordinate(sTime);
          let x2 = timeScale.timeToCoordinate(eTime);
          if (x1 === null) return;
          if (x2 === null || x2 < x1) x2 = canvas.width - 60;

          const yTop = candleSeries.priceToCoordinate(fvg.top);
          const yBottom = candleSeries.priceToCoordinate(fvg.bottom);
          if (yTop === null || yBottom === null) return;

          const boxY = Math.min(yTop, yBottom);
          const boxH = Math.max(Math.abs(yBottom - yTop), 3);
          const boxW = Math.max(x2 - x1, 15);

          ctx.save();
          ctx.fillStyle = hexToRgba(col, fvg.is_mitigated ? fvgFillOpacity * 0.4 : fvgFillOpacity);
          ctx.fillRect(x1, boxY, boxW, boxH);

          ctx.strokeStyle = hexToRgba(col, fvg.is_mitigated ? 0.25 : 0.65);
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 2]);
          ctx.strokeRect(x1, boxY, boxW, boxH);

          if (showLabels) {
            ctx.fillStyle = hexToRgba(col, 0.9);
            ctx.font = "bold 8.5px monospace";
            ctx.fillText("FVG", x1 + 3, boxY + 10);
          }
          ctx.restore();
        });
      }

      // 2.3 Structure Breaks (BOS / CHoCH) - Culled
      const breaks = classicalSMCData.structureBreaks || [];
      breaks.forEach((sb) => {
        const isBOS = sb.type === "BOS";
        if (isBOS && strategyParams?.showBOS === false) return;
        if (!isBOS && strategyParams?.showCHoCH === false) return;

        const fromT = sb.fromTime;
        const toT = sb.time;
        if (visibleRange) {
          if (toT && toT < visibleRange.from) return;
          if (fromT && fromT > visibleRange.to) return;
        }

        const col = isBOS ? bosCol : chochCol;

        const x1 = timeScale.timeToCoordinate(fromT);
        const x2 = timeScale.timeToCoordinate(toT);
        if (x1 === null || x2 === null) return;

        const y = candleSeries.priceToCoordinate(sb.price);
        if (y === null) return;

        ctx.save();
        ctx.strokeStyle = col;
        ctx.lineWidth = 1.2;
        ctx.setLineDash(isBOS ? [3, 3] : [5, 2]);
        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.stroke();

        if (showLabels) {
          const midX = (x1 + x2) / 2;
          const label = `${sb.type} (${sb.bias === "BULLISH" ? "+" : "-"})`;
          ctx.font = "bold 8.5px monospace";
          const textW = ctx.measureText(label).width;

          ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
          ctx.fillRect(midX - textW / 2 - 3, y - 6, textW + 6, 12);
          ctx.strokeStyle = col;
          ctx.lineWidth = 1;
          ctx.setLineDash([]);
          ctx.strokeRect(midX - textW / 2 - 3, y - 6, textW + 6, 12);
          ctx.fillStyle = col;
          ctx.fillText(label, midX - textW / 2, y + 3.5);
        }
        ctx.restore();
      });
    }

    // 3. Draw MSB Dynamic (Anchor, Mid, Same, TP1, TP2, SL)
    if (isMSBDynamicActive && smcData) {
      const smcSt = indicatorStyles.smc || {};
      const vLines = smcData.visual_lines || [];
      const visibleRange = timeScale.getVisibleRange();

      vLines.forEach((ln) => {
        const typeKey = (ln.type || "").toLowerCase(); // "anchor", "mid", "same", "tp1", "tp2", "sl", "entry"
        if (typeKey === "anchor" || typeKey === "mid" || typeKey === "same") return;

        const lineCfg = smcSt[typeKey] || {};

        if (lineCfg.visible === false) return;
        if (typeKey.startsWith("tp") && strategyParams?.showTP === false) return;
        if (typeKey === "sl" && strategyParams?.showSL === false) return;

        const sTime = ln.start_time || ln.startTime;
        const eTime = ln.end_time || ln.endTime || (bars && bars[bars.length - 1]?.time);

        let x1 = timeScale.timeToCoordinate(sTime);
        let x2 = timeScale.timeToCoordinate(eTime);

        // Handle off-screen coordinates seamlessly so lines stay visible when panning/scrolling
        if (x1 === null) {
          if (visibleRange && sTime < visibleRange.from) {
            x1 = -10; // Starts before visible window, draw from left edge
          } else if (visibleRange && sTime > visibleRange.to) {
            return; // Not reached yet
          } else {
            x1 = -10;
          }
        }

        const lastBarTime = bars && bars[bars.length - 1]?.time;
        const isClosedLine = ln.is_closed || (eTime && lastBarTime && eTime < lastBarTime);

        if (x2 === null) {
          if (visibleRange && eTime < visibleRange.from) {
            return; // Already passed to the left
          } else {
            x2 = canvas.width - 60; // Extends to the right
          }
        }

        if (x2 < x1) x2 = canvas.width - 60;
        if ((typeKey.startsWith("tp") || typeKey === "sl" || typeKey === "entry") && !isClosedLine) {
          x2 = canvas.width - 60;
        }

        const y = candleSeries.priceToCoordinate(ln.price);
        if (y === null || y < -100 || y > canvas.height + 100) return;

        const col = lineCfg.color || ln.color || "#38bdf8";
        const width = lineCfg.width || (typeKey === "anchor" ? 2 : 1.2);
        let dash = [];
        if (lineCfg.style === 2) dash = [6, 4];
        else if (lineCfg.style === 1) dash = [2, 2];
        else if (lineCfg.style === 0) dash = [];
        else {
          dash = typeKey === "anchor" ? [4, 4] : (typeKey.startsWith("tp") ? [3, 3] : (typeKey === "sl" ? [2, 2] : []));
        }

        ctx.save();
        ctx.strokeStyle = col;
        ctx.lineWidth = width;
        ctx.setLineDash(dash);
        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.stroke();

        // Line Label Badge: Anchor badge to visible segment
        if (smcSt.showLabels !== false) {
          const label = `${ln.label}: ${ln.price}`;
          ctx.font = "bold 9px monospace";
          const textW = ctx.measureText(label).width;

          const badgeX = Math.max(8, Math.min(x1 + 4, canvas.width - textW - 75));

          ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
          ctx.fillRect(badgeX, y - 6, textW + 6, 13);
          ctx.strokeStyle = col;
          ctx.lineWidth = 1;
          ctx.setLineDash([]);
          ctx.strokeRect(badgeX, y - 6, textW + 6, 13);

          ctx.fillStyle = col;
          ctx.fillText(label, badgeX + 3, y + 4);
        }
        ctx.restore();
      });
    }

    // 3. Draw Pine Script Visual Lines & Labels - Culled
    if (pineResult && pineResult.visual_lines && pineResult.visual_lines.length > 0) {
      pineResult.visual_lines.forEach((ln) => {
        if (visibleRange) {
          if (ln.x2 && ln.x2 < visibleRange.from) return;
          if (ln.x1 && ln.x1 > visibleRange.to) return;
        }
        let x1 = timeScale.timeToCoordinate(ln.x1);
        let x2 = timeScale.timeToCoordinate(ln.x2);
        if (x1 === null) x1 = -10;
        if (x2 === null) x2 = canvas.width - 60;
        const y1 = candleSeries.priceToCoordinate(ln.y1);
        const y2 = candleSeries.priceToCoordinate(ln.y2 !== undefined ? ln.y2 : ln.y1);
        if (y1 === null || y2 === null) return;

        ctx.save();
        ctx.strokeStyle = ln.color || "#38bdf8";
        ctx.lineWidth = ln.width || 1.5;
        ctx.setLineDash(ln.style === 2 ? [6, 4] : (ln.style === 1 ? [2, 2] : []));
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.restore();
      });
    }

    if (pineResult && pineResult.labels && pineResult.labels.length > 0) {
      pineResult.labels.forEach((lb) => {
        if (visibleRange) {
          if (lb.time < visibleRange.from || lb.time > visibleRange.to) return;
        }
        const x = timeScale.timeToCoordinate(lb.time);
        const y = candleSeries.priceToCoordinate(lb.price);
        if (x === null || y === null) return;

        ctx.save();
        ctx.font = "bold 9.5px monospace";
        const textW = ctx.measureText(lb.text).width;
        ctx.fillStyle = lb.color || "rgba(15, 23, 42, 0.85)";
        ctx.fillRect(x - textW / 2 - 4, y - 9, textW + 8, 15);
        ctx.strokeStyle = lb.textColor || "#38bdf8";
        ctx.lineWidth = 1;
        ctx.strokeRect(x - textW / 2 - 4, y - 9, textW + 8, 15);
        ctx.fillStyle = lb.textColor || "#ffffff";
        ctx.fillText(lb.text, x - textW / 2, y + 2.5);
        ctx.restore();
      });
    }

    // 4. Draw HMA (Hull Moving Average) with dynamic Bull/Bear trend coloring (Spatially Culled & Path Batched)
    if (isHMAActive && hmaData && hmaData.hma && hmaData.hma.length >= 2) {
      const hmaSt = indicatorStyles.hma || {};
      if (hmaSt.visible !== false) {
        const bullCol = hmaSt.bullColor || "#10b981";
        const bearCol = hmaSt.bearColor || "#ef4444";
        const lineW = hmaSt.width || 2;
        const lineStyle = hmaSt.style || 0;
        const points = hmaData.hma;

        const { start, end } = visibleRange
          ? findVisibleTimeRangeIndices(points, visibleRange.from, visibleRange.to)
          : { start: 0, end: points.length - 1 };

        ctx.save();
        ctx.lineWidth = lineW;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        if (lineStyle === 2) {
          ctx.setLineDash([6, 4]);
        } else if (lineStyle === 1) {
          ctx.setLineDash([2, 2]);
        } else {
          ctx.setLineDash([]);
        }

        let currentBull = null;
        ctx.beginPath();
        for (let i = start; i < end; i++) {
          const p1 = points[i];
          const p2 = points[i + 1];

          const x1 = timeScale.timeToCoordinate(p1.time);
          const x2 = timeScale.timeToCoordinate(p2.time);
          if (x1 === null || x2 === null) continue;

          const y1 = candleSeries.priceToCoordinate(p1.value);
          const y2 = candleSeries.priceToCoordinate(p2.value);
          if (y1 === null || y2 === null) continue;

          if (p2.isBull !== currentBull) {
            if (currentBull !== null) {
              ctx.stroke();
              ctx.beginPath();
            }
            currentBull = p2.isBull;
            ctx.strokeStyle = currentBull ? bullCol : bearCol;
          }

          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
        }
        ctx.stroke();
        ctx.restore();
      }
    }

    // 5. Draw HMA Breakout Pro (Base Boxes + HMA Line + Trade Lines)
    if (isHMABreakoutActive && hmaSystemData) {
      const hmaSysSt = indicatorStyles.hmaSystem || {};
      const buyBoxCol = hmaSysSt.buyBoxColor || "#06b6d4";
      const sellBoxCol = hmaSysSt.sellBoxColor || "#f59e0b";

      // A. Draw Base Box (Latest Setup Extreme Highlight Box Only - Admin Privacy Controlled)
      const canShowBaseBox = Boolean(strategyParams?.hmaShowBaseBox) && hmaSysSt.showBaseBox !== false;
      if (canShowBaseBox) {
        const boxes = hmaSystemData.base_boxes || hmaSystemData.baseBoxes || [];
        const latestBox = boxes.length > 0 ? boxes[boxes.length - 1] : null;
        if (latestBox) {
          const bx = latestBox;
          if (!visibleRange || (bx.x2 >= visibleRange.from && bx.x1 <= visibleRange.to)) {
            const x1 = timeScale.timeToCoordinate(bx.x1);
            const x2 = timeScale.timeToCoordinate(bx.x2);
            if (x1 !== null || x2 !== null) {
              const drawX1 = x1 !== null ? x1 : -20;
              const drawX2 = x2 !== null ? x2 : canvas.width + 20;

              const yLow = candleSeries.priceToCoordinate(bx.y1);
              const yHigh = candleSeries.priceToCoordinate(bx.y2);
              if (yLow !== null && yHigh !== null) {
                const boxTop = Math.min(yLow, yHigh);
                const boxHeight = Math.max(4, Math.abs(yLow - yHigh));
                const boxLeft = Math.min(drawX1, drawX2);
                const boxWidth = Math.max(8, Math.abs(drawX2 - drawX1));

                ctx.save();
                const isBuy = bx.type === "BUY_BASE";
                const baseColor = isBuy ? buyBoxCol : sellBoxCol;
                
                // Translucent fill
                ctx.fillStyle = hexToRgba(baseColor, 0.18);
                ctx.fillRect(boxLeft, boxTop, boxWidth, boxHeight);

                // Dashed outline
                ctx.strokeStyle = baseColor;
                ctx.lineWidth = 1.5;
                ctx.setLineDash([4, 3]);
                ctx.strokeRect(boxLeft, boxTop, boxWidth, boxHeight);

                // Label badge
                if (hmaSysSt.showLabels !== false && boxLeft > -50 && boxLeft < canvas.width - 20) {
                  ctx.font = "bold 9px monospace";
                  const labelText = isBuy ? "▼ Base Low" : "▲ Base High";
                  const textW = ctx.measureText(labelText).width;
                  ctx.fillStyle = hexToRgba(baseColor, 0.85);
                  ctx.fillRect(boxLeft, boxTop - 12, textW + 6, 12);
                  ctx.fillStyle = "#ffffff";
                  ctx.fillText(labelText, boxLeft + 3, boxTop - 3);
                }
                ctx.restore();
              }
            }
          }
        }
      }

      // B. Draw HMA Line (Admin Privacy Controlled & if not already drawn by standalone HMA indicator - Batched & Culled)
      const canShowHmaLine = strategyParams?.hmaShowLine !== false;
      if (canShowHmaLine && !isHMAActive && hmaSystemData.hma && hmaSystemData.hma.length >= 2) {
        const hmaSt = hmaSysSt.hmaLine || {};
        if (hmaSt.visible !== false) {
          const bullCol = hmaSt.bullColor || "#10b981";
          const bearCol = hmaSt.bearColor || "#ef4444";
          const lineW = hmaSt.width || 2;
          const points = hmaSystemData.hma;

          const { start, end } = visibleRange
            ? findVisibleTimeRangeIndices(points, visibleRange.from, visibleRange.to)
            : { start: 0, end: points.length - 1 };

          ctx.save();
          ctx.lineWidth = lineW;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";

          let currentBull = null;
          ctx.beginPath();
          for (let i = start; i < end; i++) {
            const p1 = points[i];
            const p2 = points[i + 1];
            const x1 = timeScale.timeToCoordinate(p1.time);
            const x2 = timeScale.timeToCoordinate(p2.time);
            if (x1 === null || x2 === null) continue;
            const y1 = candleSeries.priceToCoordinate(p1.value);
            const y2 = candleSeries.priceToCoordinate(p2.value);
            if (y1 === null || y2 === null) continue;

            if (p2.isBull !== currentBull) {
              if (currentBull !== null) {
                ctx.stroke();
                ctx.beginPath();
              }
              currentBull = p2.isBull;
              ctx.strokeStyle = currentBull ? bullCol : bearCol;
            }

            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
          }
          ctx.stroke();
          ctx.restore();
        }
      }

      // C. Draw Visual Trade Lines (Entry, SL, TP1, TP2) - Culled
      const vLines = hmaSystemData.visual_lines || hmaSystemData.visualLines || [];
      vLines.forEach((ln) => {
        if (visibleRange) {
          if (ln.x2 && ln.x2 < visibleRange.from) return;
          if (ln.x1 && ln.x1 > visibleRange.to) return;
        }
        let x1 = timeScale.timeToCoordinate(ln.x1);
        let x2 = timeScale.timeToCoordinate(ln.x2);
        if (x1 === null && x2 === null) return;
        if (x1 === null) x1 = -20;
        if (x2 === null) x2 = canvas.width - 60;

        const y = candleSeries.priceToCoordinate(ln.y1);
        if (y === null) return;

        ctx.save();
        ctx.strokeStyle = ln.color || "#facc15";
        ctx.lineWidth = ln.width || 1.5;
        if (ln.style === 2) ctx.setLineDash([6, 4]);
        else if (ln.style === 1) ctx.setLineDash([2, 2]);
        else ctx.setLineDash([]);

        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.stroke();

        if (hmaSysSt.showLabels !== false && ln.label && x2 < canvas.width) {
          ctx.font = "bold 9.5px monospace";
          const labelW = ctx.measureText(ln.label).width;
          const badgeX = Math.min(x2 + 4, canvas.width - labelW - 12);
          ctx.fillStyle = hexToRgba(ln.color, 0.2);
          ctx.fillRect(badgeX, y - 7, labelW + 6, 14);
          ctx.strokeStyle = ln.color;
          ctx.lineWidth = 1;
          ctx.strokeRect(badgeX, y - 7, labelW + 6, 14);
          ctx.fillStyle = ln.color;
          ctx.fillText(ln.label, badgeX + 3, y + 4);
        }
        ctx.restore();
      });

      // D. Draw Breakout Candle Signal Badges & Arrows on Entry Candles - Culled
      const sigList = hmaSystemData.signals || hmaSystemData.recent_signals || [];
      sigList.forEach((sig) => {
        if (visibleRange) {
          if (sig.time < visibleRange.from || sig.time > visibleRange.to) return;
        }
        const x = timeScale.timeToCoordinate(sig.time);
        if (x === null || x < -50 || x > canvas.width + 50) return;
        const isBuy = sig.action === "BUY";
        const entryY = candleSeries.priceToCoordinate(sig.entry || sig.price);
        if (entryY === null) return;

        ctx.save();
        const col = isBuy ? "#10b981" : "#ef4444";
        const label = isBuy ? "▲ BUY" : "▼ SELL";
        ctx.font = "bold 10px monospace";
        const textW = ctx.measureText(label).width;

        const badgeY = isBuy ? Math.min(canvas.height - 25, entryY + 26) : Math.max(25, entryY - 26);

        // Pointer dashed line
        ctx.strokeStyle = col;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(x, isBuy ? entryY + 4 : entryY - 4);
        ctx.lineTo(x, isBuy ? badgeY - 8 : badgeY + 8);
        ctx.stroke();

        // Badge Background
        ctx.setLineDash([]);
        ctx.fillStyle = isBuy ? "rgba(6, 78, 59, 0.92)" : "rgba(127, 29, 29, 0.92)";
        ctx.fillRect(x - textW / 2 - 5, badgeY - 8, textW + 10, 16);
        ctx.strokeStyle = col;
        ctx.lineWidth = 1.2;
        ctx.strokeRect(x - textW / 2 - 5, badgeY - 8, textW + 10, 16);

        // Label Text
        ctx.fillStyle = "#ffffff";
        ctx.fillText(label, x - textW / 2, badgeY + 4);
        ctx.restore();
      });
    }

      // 6. Draw Bob Volman Price Action Scalping Pro (Barriers + Trendlines + Buildup Boxes + Trade Lines + Markers)
      // Note: 20 EMA is rendered directly by GPU-accelerated Lightweight Charts LineSeries (bobVolmanEmaRef) for silky smooth 60 FPS
      if (isBobVolmanActive && bobVolmanData) {
        const bvSt = indicatorStyles.bobVolman || {};
        const barrierCol = bvSt.barrierColor || "#f59e0b";
        const tlCol = bvSt.trendlineColor || "#c084fc";
        const buyBoxCol = bvSt.buyBuildupColor || "#06b6d4";
        const sellBoxCol = bvSt.sellBuildupColor || "#f43f5e";

        // B. Horizontal Barriers (Support / Resistance) - Culled
        if (Boolean(strategyParams?.volmanShowBarriers) && bobVolmanData.barriers) {
          bobVolmanData.barriers.forEach((barr) => {
            if (visibleRange) {
              if (barr.x2 && barr.x2 < visibleRange.from) return;
              if (barr.x1 && barr.x1 > visibleRange.to) return;
            }
            let x1 = timeScale.timeToCoordinate(barr.x1);
            let x2 = timeScale.timeToCoordinate(barr.x2);
            if (x1 === null && x2 === null) return;
            if (x1 === null) x1 = -20;
            if (x2 === null) x2 = canvas.width - 60;
            const y = candleSeries.priceToCoordinate(barr.y);
            if (y === null) return;

            ctx.save();
            ctx.strokeStyle = barr.type === "RESISTANCE" ? "#ef4444" : "#10b981";
            ctx.lineWidth = 1.5;
            ctx.setLineDash([5, 4]);
            ctx.beginPath();
            ctx.moveTo(x1, y);
            ctx.lineTo(x2, y);
            ctx.stroke();

            if (bvSt.showLabels !== false && x2 < canvas.width) {
              ctx.font = "bold 9px monospace";
              const label = `${barr.type === "RESISTANCE" ? "CẢN KHÁNG CỰ" : "CẢN HỖ TRỢ"} (${barr.touches} chạm): ${barr.y}`;
              const textW = ctx.measureText(label).width;
              const badgeX = Math.min(x2 + 4, canvas.width - textW - 10);
              ctx.fillStyle = "rgba(15, 23, 42, 0.88)";
              ctx.fillRect(badgeX, y - 6, textW + 6, 13);
              ctx.strokeStyle = barr.type === "RESISTANCE" ? "#ef4444" : "#10b981";
              ctx.lineWidth = 1;
              ctx.setLineDash([]);
              ctx.strokeRect(badgeX, y - 6, textW + 6, 13);
              ctx.fillStyle = barr.type === "RESISTANCE" ? "#fca5a5" : "#86efac";
              ctx.fillText(label, badgeX + 3, y + 4);
            }
            ctx.restore();
          });
        }

        // C. Trendlines - Culled
        if (Boolean(strategyParams?.volmanShowTrendlines) && bobVolmanData.trendlines) {
          bobVolmanData.trendlines.forEach((tl) => {
            if (visibleRange) {
              if (tl.x2 && tl.x2 < visibleRange.from) return;
              if (tl.x1 && tl.x1 > visibleRange.to) return;
            }
            let x1 = timeScale.timeToCoordinate(tl.x1);
            let x2 = timeScale.timeToCoordinate(tl.x2);
            if (x1 === null || x2 === null) return;
            const y1 = candleSeries.priceToCoordinate(tl.y1);
            const y2 = candleSeries.priceToCoordinate(tl.y2);
            if (y1 === null || y2 === null) return;

            ctx.save();
            ctx.strokeStyle = tlCol;
            ctx.lineWidth = 1.5;
            ctx.setLineDash([6, 3]);
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();

            if (bvSt.showLabels !== false) {
              ctx.font = "bold 9px monospace";
              const label = `TRENDLINE (${tl.type === "DESCENDING" ? "GIẢM" : "TĂNG"})`;
              const textW = ctx.measureText(label).width;
              ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
              ctx.fillRect(x2 + 4, y2 - 6, textW + 6, 13);
              ctx.strokeStyle = tlCol;
              ctx.lineWidth = 1;
              ctx.setLineDash([]);
              ctx.strokeRect(x2 + 4, y2 - 6, textW + 6, 13);
              ctx.fillStyle = "#e9d5ff";
              ctx.fillText(label, x2 + 7, y2 + 4);
            }
            ctx.restore();
          });
        }

        // D. Buildup Squeeze Boxes - Culled
        if (Boolean(strategyParams?.volmanShowBuildup) && bobVolmanData.buildup_boxes) {
          bobVolmanData.buildup_boxes.forEach((bx) => {
            if (visibleRange) {
              if (bx.x2 && bx.x2 < visibleRange.from) return;
              if (bx.x1 && bx.x1 > visibleRange.to) return;
            }
            const x1 = timeScale.timeToCoordinate(bx.x1);
            const x2 = timeScale.timeToCoordinate(bx.x2);
            if (x1 === null && x2 === null) return;
            const drawX1 = x1 !== null ? x1 : -20;
            const drawX2 = x2 !== null ? x2 : canvas.width + 20;

            const yLow = candleSeries.priceToCoordinate(bx.y2);
            const yHigh = candleSeries.priceToCoordinate(bx.y1);
            if (yLow === null || yHigh === null) return;

            const boxTop = Math.min(yLow, yHigh);
            const boxHeight = Math.max(4, Math.abs(yLow - yHigh));
            const boxLeft = Math.min(drawX1, drawX2);
            const boxWidth = Math.max(8, Math.abs(drawX2 - drawX1));

            const isBuy = bx.type === "BUY";
            const baseColor = isBuy ? buyBoxCol : sellBoxCol;

            ctx.save();
            ctx.fillStyle = hexToRgba(baseColor, 0.18);
            ctx.fillRect(boxLeft, boxTop, boxWidth, boxHeight);
            ctx.strokeStyle = baseColor;
            ctx.lineWidth = 1.2;
            ctx.setLineDash([3, 3]);
            ctx.strokeRect(boxLeft, boxTop, boxWidth, boxHeight);

            if (bvSt.showLabels !== false && boxLeft > -50 && boxLeft < canvas.width - 20) {
              ctx.font = "bold 8.5px monospace";
              const labelText = "⚡ Buildup Squeeze";
              const textW = ctx.measureText(labelText).width;
              ctx.fillStyle = hexToRgba(baseColor, 0.85);
              ctx.fillRect(boxLeft, boxTop - 12, textW + 6, 12);
              ctx.fillStyle = "#ffffff";
              ctx.fillText(labelText, boxLeft + 3, boxTop - 3);
            }
            ctx.restore();
          });
        }

        // E. Visual Trade Lines (Entry, SL, TP1, TP2) - Culled
        const vLines = bobVolmanData.visual_lines || bobVolmanData.visualLines || [];
        vLines.forEach((ln) => {
          if (visibleRange) {
            if (ln.x2 && ln.x2 < visibleRange.from) return;
            if (ln.x1 && ln.x1 > visibleRange.to) return;
          }
          let x1 = timeScale.timeToCoordinate(ln.x1);
          let x2 = timeScale.timeToCoordinate(ln.x2);
          if (x1 === null && x2 === null) return;
          if (x1 === null) x1 = -20;
          if (x2 === null) x2 = canvas.width - 60;
          const y = candleSeries.priceToCoordinate(ln.y1);
          if (y === null) return;

          ctx.save();
          ctx.strokeStyle = ln.color || "#06b6d4";
          ctx.lineWidth = ln.width || 1.5;
          if (ln.style === 2) ctx.setLineDash([6, 4]);
          else if (ln.style === 1) ctx.setLineDash([2, 2]);
          else ctx.setLineDash([]);

          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.stroke();

          if (bvSt.showLabels !== false && ln.label && x2 < canvas.width) {
            ctx.font = "bold 9.5px monospace";
            const labelW = ctx.measureText(ln.label).width;
            const badgeX = Math.min(x2 + 4, canvas.width - labelW - 12);
            ctx.fillStyle = hexToRgba(ln.color, 0.2);
            ctx.fillRect(badgeX, y - 7, labelW + 6, 14);
            ctx.strokeStyle = ln.color;
            ctx.lineWidth = 1;
            ctx.strokeRect(badgeX, y - 7, labelW + 6, 14);
            ctx.fillStyle = ln.color;
            ctx.fillText(ln.label, badgeX + 3, y + 4);
          }
          ctx.restore();
        });

        // F. Setup Signal Badges & Arrows on Entry Candles - Culled
        const sigList = bobVolmanData.signals || bobVolmanData.recent_signals || [];
        sigList.forEach((sig) => {
          if (visibleRange) {
            if (sig.time < visibleRange.from || sig.time > visibleRange.to) return;
          }
          const x = timeScale.timeToCoordinate(sig.time);
          if (x === null || x < -50 || x > canvas.width + 50) return;
          const isBuy = sig.action === "BUY";
          const entryY = candleSeries.priceToCoordinate(sig.entry || sig.price);
          if (entryY === null) return;

          ctx.save();
          const col = isBuy ? "#06b6d4" : "#f43f5e";
          const setupTag = sig.setup_type || "BV";
          const label = isBuy ? `▲ ${setupTag} BUY` : `▼ ${setupTag} SELL`;
          ctx.font = "bold 10px monospace";
          const textW = ctx.measureText(label).width;
          const badgeY = isBuy ? Math.min(canvas.height - 25, entryY + 26) : Math.max(25, entryY - 26);

          ctx.strokeStyle = col;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([2, 2]);
          ctx.beginPath();
          ctx.moveTo(x, isBuy ? entryY + 4 : entryY - 4);
          ctx.lineTo(x, isBuy ? badgeY - 8 : badgeY + 8);
          ctx.stroke();

          ctx.setLineDash([]);
          ctx.fillStyle = isBuy ? "rgba(8, 51, 68, 0.95)" : "rgba(76, 5, 25, 0.95)";
          ctx.fillRect(x - textW / 2 - 5, badgeY - 8, textW + 10, 16);
          ctx.strokeStyle = col;
          ctx.lineWidth = 1.2;
          ctx.strokeRect(x - textW / 2 - 5, badgeY - 8, textW + 10, 16);

          ctx.fillStyle = "#ffffff";
          ctx.fillText(label, x - textW / 2, badgeY + 4);
          ctx.restore();
        });
      }

      // 6E. Draw ORB Session Boxes & Signals - Culled
      if (isOrbSessionActive && orbSessionData) {
        const orbSt = indicatorStyles.orbSession || {};
        const sessionBoxes = orbSessionData.session_boxes || orbSessionData.sessionBoxes || [];

        sessionBoxes.forEach((bx) => {
          const bxTime = bx.time || bx.startTime;
          const bxEndTime = bx.end_time || bx.endTime || (bxTime + 4 * 3600);
          if (visibleRange) {
            if (bxEndTime < visibleRange.from) return;
            if (bxTime > visibleRange.to) return;
          }
          let x1 = timeScale.timeToCoordinate(bxTime);
          let x2 = timeScale.timeToCoordinate(bxEndTime);
          if (x1 === null && x2 === null) return;
          if (x1 === null) x1 = -50;
          if (x2 === null) x2 = canvas.width + 50;

          const yHigh = candleSeries.priceToCoordinate(bx.high);
          const yLow = candleSeries.priceToCoordinate(bx.low);
          if (yHigh === null || yLow === null) return;

          const boxTop = Math.min(yHigh, yLow);
          const boxH = Math.max(Math.abs(yLow - yHigh), 2);
          const boxLeft = Math.min(x1, x2);
          const boxW = Math.max(Math.abs(x2 - x1), 10);

          const sName = bx.session_name || bx.name || "ORB";
          const isAsia = sName.includes("Á") || sName.includes("Asia");
          const isLondon = sName.includes("London") || sName.includes("Âu");
          const baseColor = isAsia 
            ? (orbSt.asiaBoxColor || "#38bdf8") 
            : isLondon 
            ? (orbSt.londonBoxColor || "#eab308") 
            : (orbSt.nyBoxColor || "#a855f7");

          ctx.save();
          // Shaded fill
          ctx.fillStyle = hexToRgba(baseColor, 0.15);
          ctx.fillRect(boxLeft, boxTop, boxW, boxH);

          // Top and Bottom dashed border
          ctx.strokeStyle = baseColor;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(boxLeft, boxTop, boxW, boxH);

          // Label badge on top left of box
          ctx.font = "bold 9.5px monospace";
          const labelText = `🕒 ${sName} [${Number(bx.low).toFixed(2)} - ${Number(bx.high).toFixed(2)}]`;
          const textW = ctx.measureText(labelText).width;
          ctx.fillStyle = hexToRgba(baseColor, 0.85);
          ctx.fillRect(boxLeft, boxTop - 14, textW + 6, 14);
          ctx.fillStyle = "#ffffff";
          ctx.fillText(labelText, boxLeft + 3, boxTop - 3);
          ctx.restore();
        });

        // Visual trade lines (Entry, SL, TP) - Culled
        const vLines = orbSessionData.visual_lines || orbSessionData.visualLines || [];
        vLines.forEach((ln) => {
          if (visibleRange) {
            if (ln.x2 && ln.x2 < visibleRange.from) return;
            if (ln.x1 && ln.x1 > visibleRange.to) return;
          }
          let x1 = timeScale.timeToCoordinate(ln.x1);
          let x2 = timeScale.timeToCoordinate(ln.x2);
          if (x1 === null && x2 === null) return;
          if (x1 === null) x1 = -20;
          if (x2 === null) x2 = canvas.width - 60;
          const y = candleSeries.priceToCoordinate(ln.y1);
          if (y === null) return;

          ctx.save();
          ctx.strokeStyle = ln.color || "#38bdf8";
          ctx.lineWidth = ln.width || 1.5;
          if (ln.style === 2) ctx.setLineDash([6, 4]);
          else if (ln.style === 1) ctx.setLineDash([2, 2]);
          else ctx.setLineDash([]);

          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.stroke();

          if (orbSt.showLabels !== false && ln.label && x2 < canvas.width) {
            ctx.font = "bold 9.5px monospace";
            const labelW = ctx.measureText(ln.label).width;
            const badgeX = Math.min(x2 + 4, canvas.width - labelW - 12);
            ctx.fillStyle = hexToRgba(ln.color, 0.2);
            ctx.fillRect(badgeX, y - 7, labelW + 6, 14);
            ctx.strokeStyle = ln.color;
            ctx.lineWidth = 1;
            ctx.strokeRect(badgeX, y - 7, labelW + 6, 14);
            ctx.fillStyle = ln.color;
            ctx.fillText(ln.label, badgeX + 3, y + 4);
          }
          ctx.restore();
        });
      }

      // 6F. Draw Liquidity Sweep & Market Structure Shift (MSS Pro) - Culled
      // 6F. Draw Liquidity Sweeps (Boxes, Rejection Wick Lines & Clean Mitigations)
      if (isLiquiditySweepActive && liquiditySweepData) {
        const sweepSt = indicatorStyles.liquiditySweep || {};
        const showLabels = sweepSt.showLabels !== false;
        const showZones = sweepSt.showZones === true;

        // A. Draw Sweep Area Boxes & Width-2.5 Rejection Wick Lines (Only when showZones is enabled)
        if (showZones) {
          const sweepBoxes = liquiditySweepData.sweep_boxes || liquiditySweepData.sweepBoxes || [];
          const activeSigTime = liquiditySweepData.latest_signal?.time || liquiditySweepData.latestSignal?.time;
          sweepBoxes.forEach((bx) => {
          // Clean chart display: Do NOT draw broken/invalidated historical boxes across the screen
          const isLatestSetup = activeSigTime && (bx.sweep_bar_time === activeSigTime || bx.left_time === activeSigTime);
          if (bx.broken && !isLatestSetup) return;
          if (visibleRange) {
            if (bx.right_time && bx.right_time < visibleRange.from) return;
            if (bx.left_time && bx.left_time > visibleRange.to) return;
          }
          let x1 = timeScale.timeToCoordinate(bx.left_time);
          let x2 = timeScale.timeToCoordinate(bx.right_time);
          if (x1 === null && x2 === null) return;
          if (x1 === null) x1 = -20;
          if (x2 === null) x2 = canvas.width - 60;

          const yTop = candleSeries.priceToCoordinate(bx.top);
          const yBottom = candleSeries.priceToCoordinate(bx.bottom);
          if (yTop === null || yBottom === null) return;

          const minX = Math.min(x1, x2);
          const boxW = Math.max(Math.abs(x2 - x1), 6);
          const minY = Math.min(yTop, yBottom);
          const boxH = Math.max(Math.abs(yBottom - yTop), 3);

          ctx.save();
          const isBull = bx.direction === -1;
          const boxFill = isBull ? "rgba(56, 189, 248, 0.20)" : "rgba(244, 63, 94, 0.20)";
          const boxBorder = isBull ? "#38bdf8" : "#f43f5e";

          // 1. Translucent background fill
          ctx.fillStyle = boxFill;
          ctx.fillRect(minX, minY, boxW, boxH);

          // 2. Crisp 4-corner bounding rectangle border (matching user's hand-drawn blue box!)
          ctx.strokeStyle = boxBorder;
          ctx.lineWidth = 1.8;
          ctx.strokeRect(minX, minY, boxW, boxH);

          // 3. Small badge label
          ctx.font = "bold 10px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
          ctx.fillStyle = boxBorder;
          const labelText = isBull ? "SWEEP BUY ZONE" : "SWEEP SELL ZONE";
          ctx.fillText(labelText, minX + 4, minY > 15 ? minY - 4 : minY + 12);

          // Draw Width 2.5 Rejection Wick Line at Sweep Candle
          if (bx.sweep_bar_time) {
            const xSweep = timeScale.timeToCoordinate(bx.sweep_bar_time);
            if (xSweep !== null && xSweep >= -20 && xSweep <= canvas.width + 20) {
              const yR1 = candleSeries.priceToCoordinate(bx.rejection_y1 || bx.top);
              const yR2 = candleSeries.priceToCoordinate(bx.rejection_y2 || bx.bottom);
              if (yR1 !== null && yR2 !== null) {
                ctx.strokeStyle = isBull ? "#089981" : "#f23645";
                ctx.lineWidth = 2.5;
                ctx.beginPath();
                ctx.moveTo(xSweep, yR1);
                ctx.lineTo(xSweep, yR2);
                ctx.stroke();
              }
            }
          }
          ctx.restore();
          });
        }

        // B. Draw Visual Trade Lines (Pivot Dotted/Dashed lines, Notches, and Active Trade Setup)
        const vLines = liquiditySweepData.visual_lines || [];
        vLines.forEach((ln) => {
          // If zones are hidden, hide historical pivot dotted/dashed lines to keep chart 100% clean
          if (!showZones && !ln.is_trade_line && ln.style !== 0) return;

          if (visibleRange) {
            if (ln.x2 && ln.x2 < visibleRange.from) return;
            if (ln.x1 && ln.x1 > visibleRange.to) return;
          }
          let x1 = timeScale.timeToCoordinate(ln.x1);
          let x2 = timeScale.timeToCoordinate(ln.x2);
          if (x1 === null && x2 === null) return;
          if (x1 === null) x1 = -20;
          if (x2 === null) x2 = canvas.width - 60;
          const y = candleSeries.priceToCoordinate(ln.y1);
          if (y === null) return;

          ctx.save();
          ctx.strokeStyle = ln.color || "#8b5cf6";
          ctx.lineWidth = ln.width || 1.0;
          if (ln.style === 2) ctx.setLineDash([4, 3]); // Dashed (Outbreak & Retest)
          else if (ln.style === 1) ctx.setLineDash([2, 3]); // Dotted (Wick Sweeps / Notches)
          else ctx.setLineDash([]); // Solid (Active Entry)

          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.stroke();

          // Only render text badge if explicitly requested by active trade lines (e.g. ENTRY, TP, SL)
          if (showLabels && ln.label && ln.is_trade_line && x2 < canvas.width) {
            ctx.font = "bold 9.5px monospace";
            const labelW = ctx.measureText(ln.label).width;
            const badgeX = Math.min(x2 + 4, canvas.width - labelW - 12);
            ctx.fillStyle = hexToRgba(ln.color, 0.2);
            ctx.fillRect(badgeX, y - 7, labelW + 6, 14);
            ctx.strokeStyle = ln.color;
            ctx.lineWidth = 1;
            ctx.strokeRect(badgeX, y - 7, labelW + 6, 14);
            ctx.fillStyle = ln.color;
            ctx.fillText(ln.label, badgeX + 3, y + 4);
          }
          ctx.restore();
        });
      }

      // 6G. Draw Scalping Pro (Boxes, Rejection Wick Lines & Clean Mitigations)
      if (isScalpingProActive && scalpingProData) {
        const scalpSt = indicatorStyles.scalpingPro || indicatorStyles.liquiditySweep || {};
        const showLabels = scalpSt.showLabels !== false;
        const showZones = scalpSt.showZones === true;

        if (showZones) {
          const sweepBoxes = scalpingProData.sweep_boxes || scalpingProData.sweepBoxes || [];
          const activeSigTime = scalpingProData.latest_signal?.time || scalpingProData.latestSignal?.time;
          sweepBoxes.forEach((bx) => {
            const isLatestSetup = activeSigTime && (bx.sweep_bar_time === activeSigTime || bx.left_time === activeSigTime);
            if (bx.broken && !isLatestSetup) return;
            if (visibleRange) {
              if (bx.right_time && bx.right_time < visibleRange.from) return;
              if (bx.left_time && bx.left_time > visibleRange.to) return;
            }
            let x1 = timeScale.timeToCoordinate(bx.left_time);
            let x2 = timeScale.timeToCoordinate(bx.right_time);
            if (x1 === null && x2 === null) return;
            if (x1 === null) x1 = -20;
            if (x2 === null) x2 = canvas.width - 60;

            const yTop = candleSeries.priceToCoordinate(bx.top);
            const yBottom = candleSeries.priceToCoordinate(bx.bottom);
            if (yTop === null || yBottom === null) return;

            const minX = Math.min(x1, x2);
            const boxW = Math.max(Math.abs(x2 - x1), 6);
            const minY = Math.min(yTop, yBottom);
            const boxH = Math.max(Math.abs(yBottom - yTop), 3);

            ctx.save();
            const isBull = bx.direction === -1;
            const boxFill = isBull ? "rgba(56, 189, 248, 0.20)" : "rgba(244, 63, 94, 0.20)";
            const boxBorder = isBull ? "#38bdf8" : "#f43f5e";

            ctx.fillStyle = boxFill;
            ctx.fillRect(minX, minY, boxW, boxH);

            ctx.strokeStyle = boxBorder;
            ctx.lineWidth = 1.8;
            ctx.strokeRect(minX, minY, boxW, boxH);

            ctx.font = "bold 10px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
            ctx.fillStyle = boxBorder;
            const labelText = isBull ? "SCALP BUY ZONE" : "SCALP SELL ZONE";
            ctx.fillText(labelText, minX + 4, minY > 15 ? minY - 4 : minY + 12);

            if (bx.sweep_bar_time) {
              const xSweep = timeScale.timeToCoordinate(bx.sweep_bar_time);
              if (xSweep !== null && xSweep >= -20 && xSweep <= canvas.width + 20) {
                const yR1 = candleSeries.priceToCoordinate(bx.rejection_y1 || bx.top);
                const yR2 = candleSeries.priceToCoordinate(bx.rejection_y2 || bx.bottom);
                if (yR1 !== null && yR2 !== null) {
                  ctx.strokeStyle = isBull ? "#089981" : "#f23645";
                  ctx.lineWidth = 2.5;
                  ctx.beginPath();
                  ctx.moveTo(xSweep, yR1);
                  ctx.lineTo(xSweep, yR2);
                  ctx.stroke();
                }
              }
            }
            ctx.restore();
          });
        }

        const vLines = scalpingProData.visual_lines || [];
        vLines.forEach((ln) => {
          if (!showZones && !ln.is_trade_line && ln.style !== 0) return;
          if (visibleRange) {
            if (ln.x2 && ln.x2 < visibleRange.from) return;
            if (ln.x1 && ln.x1 > visibleRange.to) return;
          }
          let x1 = timeScale.timeToCoordinate(ln.x1);
          let x2 = timeScale.timeToCoordinate(ln.x2);
          if (x1 === null && x2 === null) return;
          if (x1 === null) x1 = -20;
          if (x2 === null) x2 = canvas.width - 60;
          const y = candleSeries.priceToCoordinate(ln.y1);
          if (y === null) return;

          ctx.save();
          ctx.strokeStyle = ln.color || "#8b5cf6";
          ctx.lineWidth = ln.width || 1.0;
          if (ln.style === 2) ctx.setLineDash([4, 3]);
          else if (ln.style === 1) ctx.setLineDash([2, 3]);
          else ctx.setLineDash([]);

          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.stroke();

          if (showLabels && ln.label && ln.is_trade_line && x2 < canvas.width) {
            ctx.font = "bold 9.5px monospace";
            const labelW = ctx.measureText(ln.label).width;
            const badgeX = Math.min(x2 + 4, canvas.width - labelW - 12);
            ctx.fillStyle = hexToRgba(ln.color, 0.2);
            ctx.fillRect(badgeX, y - 7, labelW + 6, 14);
            ctx.strokeStyle = ln.color;
            ctx.lineWidth = 1;
            ctx.strokeRect(badgeX, y - 7, labelW + 6, 14);
            ctx.fillStyle = ln.color;
            ctx.fillText(ln.label, badgeX + 3, y + 4);
          }
          ctx.restore();
        });
      }

      // 7. Draw User Interactive Drawings (with Selection Highlight)
      renderAllDrawings(ctx, drawings, activeDrawing, selectedDrawingId, chart, candleSeries);
    } catch (err) {
      console.warn("Canvas overlay render error:", err);
    }
  }, [
    isHMAActive,
    hmaData,
    isHMABreakoutActive,
    hmaSystemData,
    isBobVolmanActive,
    bobVolmanData,
    isOrbSessionActive,
    orbSessionData,
    isScalpingProActive,
    scalpingProData,
    isLiquiditySweepActive,
    liquiditySweepData,
    isIchimokuActive, 
    ichimokuData, 
    isClassicalSMCActive,
    classicalSMCData,
    isMSBDynamicActive,
    isSMCActive, 
    smcData, 
    pineResult,
    indicatorStyles, 
    drawings, 
    activeDrawing, 
    selectedDrawingId, 
    bars, 
    strategyParams
  ]);

  const renderCanvasOverlayRef = useRef(null);
  useEffect(() => {
    renderCanvasOverlayRef.current = renderCanvasOverlay;
  });

  // Interactive Mouse Events for Drawing (Active ONLY when a drawing tool is selected)
  const getLogicalCoords = (e) => {
    const canvas = overlayCanvasRef.current;
    const chart = chartRef.current;
    const candleSeries = candleSeriesRef.current;
    if (!canvas || !chart || !candleSeries) return null;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const time = chart.timeScale().coordinateToTime(x);
    const price = candleSeries.coordinateToPrice(y);

    if (time === null || price === null || price === undefined) return null;
    return { time, price, x, y };
  };

  const handleMouseDown = (e) => {
    if (isSelectingBar) return;

    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // 1. Cursor Mode: Corner handle resizing, body moving, or selecting
    if (activeTool === "cursor") {
      const hit = findDrawingHitTarget(
        x,
        y,
        drawingsRef.current,
        selectedDrawingIdRef.current,
        chartRef.current,
        candleSeriesRef.current
      );

      if (hit.type === "handle") {
        isDraggingDrawingRef.current = true;
        dragStateRef.current = {
          type: "handle",
          handleId: hit.handleId,
          drawingId: hit.drawing.id,
          startScreenX: x,
          startScreenY: y,
          initialP1: hit.drawing.p1 ? { ...hit.drawing.p1 } : null,
          initialP2: hit.drawing.p2 ? { ...hit.drawing.p2 } : null,
          initialPrice: hit.drawing.price,
          initialP: hit.drawing.p ? { ...hit.drawing.p } : null,
        };
        setSelectedDrawingId(hit.drawing.id);
        setActionBarPos({ x, y });
        return;
      }

      if (hit.type === "body") {
        isDraggingDrawingRef.current = true;
        dragStateRef.current = {
          type: "body",
          drawingId: hit.drawing.id,
          startScreenX: x,
          startScreenY: y,
          initialP1: hit.drawing.p1 ? { ...hit.drawing.p1 } : null,
          initialP2: hit.drawing.p2 ? { ...hit.drawing.p2 } : null,
          initialPrice: hit.drawing.price,
          initialP: hit.drawing.p ? { ...hit.drawing.p } : null,
        };
        setSelectedDrawingId(hit.drawing.id);
        setActionBarPos({ x, y });
        return;
      }

      if (hit.type === "select") {
        setSelectedDrawingId(hit.drawing.id);
        setActionBarPos({ x, y });
        return;
      }

      // Hit empty space on chart: deselect & temporarily yield pointer events to chart pan
      if (selectedDrawingIdRef.current) {
        setSelectedDrawingId(null);
      }
      isPanningRef.current = true;
      canvas.style.pointerEvents = "none";
      const handleWindowMouseUp = () => {
        isPanningRef.current = false;
        if (overlayCanvasRef.current) {
          overlayCanvasRef.current.style.pointerEvents = activeToolRef.current === "cursor" ? "none" : "auto";
        }
        window.removeEventListener("mouseup", handleWindowMouseUp);
      };
      window.addEventListener("mouseup", handleWindowMouseUp);
      return;
    }

    // 2. Active Tool Drawing Creation Mode
    const coords = getLogicalCoords(e);
    if (!coords) return;

    setSelectedDrawingId(null);

    if (activeTool === "horizontal") {
      const newD = {
        id: `draw_${Date.now()}`,
        type: "horizontal",
        price: Math.round(coords.price * 100) / 100,
        color: drawColor,
        width: 1.5,
        style: 0,
      };
      handleSaveDrawings([...drawings, newD]);
      setSelectedDrawingId(newD.id);
      setActionBarPos({ x: coords.x, y: coords.y });
      setActiveTool("cursor");
      return;
    }

    if (activeTool === "text") {
      const text = window.prompt("Nhập nội dung ghi chú phân tích:");
      if (text && text.trim()) {
        const newD = {
          id: `draw_${Date.now()}`,
          type: "text",
          p: { time: coords.time, price: coords.price },
          text: text.trim(),
          color: drawColor,
        };
        handleSaveDrawings([...drawings, newD]);
        setSelectedDrawingId(newD.id);
        setActionBarPos({ x: coords.x, y: coords.y });
      }
      setActiveTool("cursor");
      return;
    }

    // Multi-point tools (trendline, rectangle, fibonacci)
    isDrawingInProgressRef.current = true;
    const fibTemplate = activeTool === "fibonacci" ? loadSavedFibTemplate() : null;
    const initial = {
      id: `draw_${Date.now()}`,
      type: activeTool,
      p1: { time: coords.time, price: coords.price },
      p2: { time: coords.time, price: coords.price },
      color: drawColor,
      width: 2,
      style: 0,
      ...(fibTemplate || {}),
    };
    setActiveDrawing(initial);
  };

  const handleMouseMove = (e) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // A. Handle Resizing or Moving existing drawing
    if (isDraggingDrawingRef.current && dragStateRef.current) {
      const drag = dragStateRef.current;
      const targetDrawing = drawingsRef.current.find((d) => d.id === drag.drawingId);
      if (!targetDrawing) return;

      const chart = chartRef.current;
      const candleSeries = candleSeriesRef.current;
      if (!chart || !candleSeries) return;

      const timeScale = chart.timeScale();

      if (drag.type === "handle") {
        const coords = getLogicalCoords(e);
        if (!coords) return;

        let updated = { ...targetDrawing };

        if (drag.handleId === "p1") {
          updated.p1 = { time: coords.time, price: coords.price };
        } else if (drag.handleId === "p2") {
          updated.p2 = { time: coords.time, price: coords.price };
        } else if (drag.handleId === "p1_x2y1") {
          // Top-right corner of rectangle (x2, y1)
          updated.p1 = { ...updated.p1, price: coords.price };
          updated.p2 = { ...updated.p2, time: coords.time };
        } else if (drag.handleId === "p1_x1y2") {
          // Bottom-left corner of rectangle (x1, y2)
          updated.p1 = { ...updated.p1, time: coords.time };
          updated.p2 = { ...updated.p2, price: coords.price };
        } else if (drag.handleId === "price") {
          updated.price = Math.round(coords.price * 100) / 100;
        } else if (drag.handleId === "p") {
          updated.p = { time: coords.time, price: coords.price };
        }

        const nextDrawings = drawingsRef.current.map((d) => (d.id === drag.drawingId ? updated : d));
        setDrawings(nextDrawings);
        drawingsRef.current = nextDrawings;
        return;
      }

      if (drag.type === "body") {
        const dx = x - drag.startScreenX;
        const dy = y - drag.startScreenY;

        let updated = { ...targetDrawing };

        if (drag.initialP1 && drag.initialP2) {
          const p1x = timeScale.timeToCoordinate(drag.initialP1.time);
          const p1y = candleSeries.priceToCoordinate(drag.initialP1.price);
          const p2x = timeScale.timeToCoordinate(drag.initialP2.time);
          const p2y = candleSeries.priceToCoordinate(drag.initialP2.price);

          if (p1x !== null && p1y !== null && p2x !== null && p2y !== null) {
            const newTime1 = timeScale.coordinateToTime(p1x + dx);
            const newPrice1 = candleSeries.coordinateToPrice(p1y + dy);
            const newTime2 = timeScale.coordinateToTime(p2x + dx);
            const newPrice2 = candleSeries.coordinateToPrice(p2y + dy);

            if (newTime1 && newPrice1 && newTime2 && newPrice2) {
              updated.p1 = { time: newTime1, price: newPrice1 };
              updated.p2 = { time: newTime2, price: newPrice2 };
            }
          }
        } else if (drag.initialPrice !== undefined) {
          const py = candleSeries.priceToCoordinate(drag.initialPrice);
          if (py !== null) {
            const newPrice = candleSeries.coordinateToPrice(py + dy);
            if (newPrice !== null) {
              updated.price = Math.round(newPrice * 100) / 100;
            }
          }
        } else if (drag.initialP) {
          const px = timeScale.timeToCoordinate(drag.initialP.time);
          const py = candleSeries.priceToCoordinate(drag.initialP.price);
          if (px !== null && py !== null) {
            const newTime = timeScale.coordinateToTime(px + dx);
            const newPrice = candleSeries.coordinateToPrice(py + dy);
            if (newTime && newPrice) {
              updated.p = { time: newTime, price: newPrice };
            }
          }
        }

        const nextDrawings = drawingsRef.current.map((d) => (d.id === drag.drawingId ? updated : d));
        setDrawings(nextDrawings);
        drawingsRef.current = nextDrawings;
        return;
      }
    }

    // B. Drawing creation in progress
    if (isDrawingInProgressRef.current && activeDrawing) {
      const coords = getLogicalCoords(e);
      if (!coords) return;

      setActiveDrawing((prev) => ({
        ...prev,
        p2: { time: coords.time, price: coords.price },
      }));
      return;
    }

    // C. Hover cursor feedback
    if (activeTool === "cursor") {
      const hit = findDrawingHitTarget(
        x,
        y,
        drawingsRef.current,
        selectedDrawingIdRef.current,
        chartRef.current,
        candleSeriesRef.current
      );
      canvas.style.cursor = hit.cursor || "crosshair";
    }
  };

  const handleMouseUp = (e) => {
    if (isDraggingDrawingRef.current) {
      isDraggingDrawingRef.current = false;
      dragStateRef.current = null;
      handleSaveDrawings(drawingsRef.current);
      return;
    }

    if (!isDrawingInProgressRef.current || !activeDrawing) return;
    isDrawingInProgressRef.current = false;

    if (activeDrawing.p1 && activeDrawing.p2) {
      handleSaveDrawings([...drawings, activeDrawing]);
      setSelectedDrawingId(activeDrawing.id);
      const coords = getLogicalCoords(e);
      if (coords) setActionBarPos({ x: coords.x, y: coords.y });
    }
    setActiveDrawing(null);
    setActiveTool("cursor");
  };

  // Initialize Main Chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const container = chartContainerRef.current;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || 460,
      layout: {
        background: { type: ColorType.Solid, color: "#090d16" },
        textColor: "#94a3b8",
        fontSize: 12,
        fontFamily: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto",
      },
      grid: {
        vertLines: { color: "rgba(30, 41, 59, 0.35)" },
        horzLines: { color: "rgba(30, 41, 59, 0.35)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: "#475569",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#1e293b",
        },
        horzLine: {
          color: "#475569",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#1e293b",
        },
      },
      rightPriceScale: {
        borderColor: "#1e293b",
        autoScale: true,
        scaleMargins: {
          top: 0.10,
          bottom: 0.18,
        },
      },
      localization: {
        locale: "en-GB",
        timeFormatter: createChartTimeFormatter(currentTimezone || "local"),
        dateFormat: "dd/MM/yyyy",
      },
      timeScale: {
        borderColor: "#1e293b",
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 10,
        barSpacing: 18,
        minBarSpacing: 0.1,
        fixLeftEdge: false,
        fixRightEdge: false,
        shiftVisibleRangeOnNewBar: false,
        tickMarkFormatter: createChartTickMarkFormatter(currentTimezone || "local"),
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;

    // Native Click Subscription: Handles Replay Click-to-Cut AND Drawing Selection
    chart.subscribeClick((param) => {
      if (isSelectingBarRef.current && param) {
        let clickedTime = param.time;
        if (!clickedTime && param.point && chartRef.current) {
          try {
            clickedTime = chartRef.current.timeScale().coordinateToTime(param.point.x);
          } catch (e) {}
        }

        const allBars = rawBarsRef.current;
        let idx = -1;
        if (clickedTime) {
          idx = allBars.findIndex((b) => b.time === clickedTime);
        } else if (param.logical !== undefined && param.logical !== null) {
          idx = Math.max(0, Math.min(allBars.length - 1, param.logical));
        }

        if (idx !== -1 && idx < allBars.length) {
          targetSeekLogicalRef.current = idx;
          if (onSeekRef.current) {
            onSeekRef.current(idx);
          }
          setIsSelectingBar(false);
        }
        return;
      }

      // Drawing Selection in Cursor Mode
      if (activeToolRef.current === "cursor" && param && param.point) {
        const { x, y } = param.point;
        const hit = findDrawingAtCoordinate(x, y, drawingsRef.current, chartRef.current, candleSeriesRef.current);
        if (hit) {
          setSelectedDrawingId(hit.id);
          setActionBarPos({ x, y });
        } else {
          setSelectedDrawingId(null);
        }
      }
    });

    // Candlestick Series
    const { precision, minMove } = getPrecision(currentSymbol);
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#10b981",
      downColor: "#ef4444",
      borderVisible: false,
      wickUpColor: "#10b981",
      wickDownColor: "#ef4444",
      priceFormat: {
        type: "price",
        precision: precision,
        minMove: minMove,
      },
    });
    candleSeriesRef.current = candleSeries;

    try {
      markersRef.current = createSeriesMarkers(candleSeries, []);
    } catch (e) {
      console.warn("Markers init:", e);
    }

    // Volume Series
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });
    volumeSeriesRef.current = volumeSeries;

    // 1. Matrix EMAs (Clean Right Scale: No indicator badges on price scale)
    ema9SeriesRef.current = chart.addSeries(LineSeries, { color: "#06b6d4", lineWidth: 1, lastValueVisible: false, priceLineVisible: false });
    ema21SeriesRef.current = chart.addSeries(LineSeries, { color: "#eab308", lineWidth: 1.5, lastValueVisible: false, priceLineVisible: false });
    ema50SeriesRef.current = chart.addSeries(LineSeries, { color: "#f97316", lineWidth: 1.5, lastValueVisible: false, priceLineVisible: false });
    ema200SeriesRef.current = chart.addSeries(LineSeries, { color: "#a855f7", lineWidth: 2, lastValueVisible: false, priceLineVisible: false });

    // 2. Bollinger Bands
    bbUpperRef.current = chart.addSeries(LineSeries, { color: "#38bdf8", lineWidth: 1, lineStyle: LineStyle.Dashed, lastValueVisible: false, priceLineVisible: false });
    bbLowerRef.current = chart.addSeries(LineSeries, { color: "#38bdf8", lineWidth: 1, lineStyle: LineStyle.Dashed, lastValueVisible: false, priceLineVisible: false });

    // 3. Ichimoku Cloud Lines
    ichiTenkanRef.current = chart.addSeries(LineSeries, { color: "#06b6d4", lineWidth: 1.5, lastValueVisible: false, priceLineVisible: false });
    ichiKijunRef.current = chart.addSeries(LineSeries, { color: "#f59e0b", lineWidth: 2, lastValueVisible: false, priceLineVisible: false });
    ichiChikouRef.current = chart.addSeries(LineSeries, { color: "#10b981", lineWidth: 1.5, lineStyle: LineStyle.Dotted, lastValueVisible: false, priceLineVisible: false });
    ichiSpanARef.current = chart.addSeries(LineSeries, { color: "#10b981", lineWidth: 1, lastValueVisible: false, priceLineVisible: false });
    ichiSpanBRef.current = chart.addSeries(LineSeries, { color: "#ef4444", lineWidth: 1, lastValueVisible: false, priceLineVisible: false });

    // 4. Multi EMA Ribbon Series
    multiEma1Ref.current = chart.addSeries(LineSeries, { color: "#38bdf8", lineWidth: 1.5, lastValueVisible: false, priceLineVisible: false });
    multiEma2Ref.current = chart.addSeries(LineSeries, { color: "#facc15", lineWidth: 2, lastValueVisible: false, priceLineVisible: false });
    multiEma3Ref.current = chart.addSeries(LineSeries, { color: "#c084fc", lineWidth: 2, lastValueVisible: false, priceLineVisible: false });
    multiEma4Ref.current = chart.addSeries(LineSeries, { color: "#fb7185", lineWidth: 2.5, lastValueVisible: false, priceLineVisible: false });

    // 5. Bob Volman 20 EMA Native Series (GPU-accelerated native series for 60 FPS)
    bobVolmanEmaRef.current = chart.addSeries(LineSeries, { color: "#06b6d4", lineWidth: 2, lastValueVisible: false, priceLineVisible: false });

    // Canvas overlay resize & sync
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        const width = chartContainerRef.current.clientWidth;
        const height = chartContainerRef.current.clientHeight || 460;
        chartRef.current.applyOptions({ width, height });

        if (overlayCanvasRef.current) {
          overlayCanvasRef.current.width = width;
          overlayCanvasRef.current.height = height;
          if (renderCanvasOverlayRef.current) {
            renderCanvasOverlayRef.current();
          }
        }
      }
    };

    let syncRafId = null;
    const triggerSync = () => {
      if (syncRafId !== null) return;
      syncRafId = requestAnimationFrame(() => {
        syncRafId = null;
        if (renderCanvasOverlayRef.current) {
          renderCanvasOverlayRef.current();
        }
      });
    };

    // Only sync on pointermove if a mouse button is actively held down (i.e. user is dragging/panning the chart or scaling an axis)
    const handlePointerMoveThrottled = (e) => {
      if (e.buttons > 0) {
        triggerSync();
      }
    };

    window.addEventListener("resize", handleResize);
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    // Native Lightweight Charts viewport range change handles smooth 60 FPS panning & zooming
    chart.timeScale().subscribeVisibleLogicalRangeChange(triggerSync);

    container.addEventListener("wheel", triggerSync, { passive: true });
    container.addEventListener("pointermove", handlePointerMoveThrottled, { passive: true });
    window.addEventListener("pointerup", triggerSync, { passive: true });

    setTimeout(() => {
      handleResize();
    }, 50);

    return () => {
      if (syncRafId !== null) {
        cancelAnimationFrame(syncRafId);
      }
      window.removeEventListener("resize", handleResize);
      container.removeEventListener("wheel", triggerSync);
      container.removeEventListener("pointermove", handlePointerMoveThrottled);
      window.removeEventListener("pointerup", triggerSync);
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        bobVolmanEmaRef.current = null;
      }
    };
  }, []);

  // Initialize RSI Sub-Chart
  useEffect(() => {
    if (!rsiContainerRef.current || !showRSI) {
      if (rsiChartRef.current) {
        rsiChartRef.current.remove();
        rsiChartRef.current = null;
      }
      return;
    }

    const rsiContainer = rsiContainerRef.current;
    if (rsiChartRef.current) {
      rsiChartRef.current.remove();
      rsiChartRef.current = null;
    }

    const rsiChart = createChart(rsiContainer, {
      width: rsiContainer.clientWidth,
      height: 120,
      layout: {
        background: { type: ColorType.Solid, color: "#090d16" },
        textColor: "#94a3b8",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "rgba(30, 41, 59, 0.3)" },
        horzLines: { color: "rgba(30, 41, 59, 0.3)" },
      },
      rightPriceScale: {
        borderColor: "#1e293b",
        scaleMargins: { top: 0.10, bottom: 0.18 },
      },
      localization: {
        locale: "en-GB",
        timeFormatter: createChartTimeFormatter(currentTimezone || "local"),
        dateFormat: "dd/MM/yyyy",
      },
      timeScale: {
        borderColor: "#1e293b",
        timeVisible: true,
        secondsVisible: false,
        visible: true,
        rightOffset: 10,
        barSpacing: 18,
        tickMarkFormatter: createChartTickMarkFormatter(currentTimezone || "local"),
      },
    });

    rsiChartRef.current = rsiChart;

    const rsiSeries = rsiChart.addSeries(LineSeries, {
      color: "#c084fc",
      lineWidth: 1.5,
      title: "RSI",
    });
    rsiSeriesRef.current = rsiSeries;

    const obLine = rsiChart.addSeries(LineSeries, { color: "rgba(239, 68, 68, 0.5)", lineWidth: 1, lineStyle: LineStyle.Dashed });
    const osLine = rsiChart.addSeries(LineSeries, { color: "rgba(16, 185, 129, 0.5)", lineWidth: 1, lineStyle: LineStyle.Dashed });

    if (indicators?.rsi && indicators.rsi.length > 0) {
      obLine.setData(indicators.rsi.map(r => ({ time: r.time, value: 70 })));
      osLine.setData(indicators.rsi.map(r => ({ time: r.time, value: 30 })));
    }

    let isSyncing = false;
    const handleMainRangeChange = (range) => {
      if (!range || isSyncing || !rsiChartRef.current) return;
      isSyncing = true;
      try {
        rsiChartRef.current.timeScale().setVisibleLogicalRange(range);
      } catch (e) {}
      isSyncing = false;
    };

    const handleRsiRangeChange = (range) => {
      if (!range || isSyncing || !chartRef.current) return;
      isSyncing = true;
      try {
        chartRef.current.timeScale().setVisibleLogicalRange(range);
      } catch (e) {}
      isSyncing = false;
    };

    chartRef.current?.timeScale().subscribeVisibleLogicalRangeChange(handleMainRangeChange);
    rsiChart.timeScale().subscribeVisibleLogicalRangeChange(handleRsiRangeChange);

    const handleRsiResize = () => {
      if (rsiContainerRef.current && rsiChartRef.current) {
        rsiChartRef.current.applyOptions({
          width: rsiContainerRef.current.clientWidth,
          height: 120,
        });
      }
    };

    window.addEventListener("resize", handleRsiResize);
    return () => {
      window.removeEventListener("resize", handleRsiResize);
      try {
        chartRef.current?.timeScale().unsubscribeVisibleLogicalRangeChange(handleMainRangeChange);
      } catch (e) {}
      if (rsiChartRef.current) {
        rsiChartRef.current.remove();
        rsiChartRef.current = null;
      }
    };
  }, [showRSI]);

  const prevBarsRef = useRef(null);
  const prevKeyRef = useRef("");
  const prevBarsLengthRef = useRef(0);
  const prevBarsFirstTimeRef = useRef(null);
  const prevBarsLastTimeRef = useRef(null);
  const pendingSnapCountRef = useRef(2);
  const isInitialLoadRef = useRef(true);
  const prevSignalKeyRef = useRef(null);
  const prevMarkersKeyRef = useRef("");
  const prevShowEMARef = useRef(showEMA);
  const prevIsBBActiveRef = useRef(isBBActive);
  const prevShowRSIRef = useRef(showRSI);

  // Update Timezone on Chart TimeScale & Localization Dynamically
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.applyOptions({
        localization: {
          timeFormatter: createChartTimeFormatter(currentTimezone || "local"),
        },
        timeScale: {
          tickMarkFormatter: createChartTickMarkFormatter(currentTimezone || "local"),
        },
      });
    }
    if (rsiChartRef.current) {
      rsiChartRef.current.applyOptions({
        localization: {
          timeFormatter: createChartTimeFormatter(currentTimezone || "local"),
        },
        timeScale: {
          tickMarkFormatter: createChartTickMarkFormatter(currentTimezone || "local"),
        },
      });
    }
  }, [currentTimezone]);

  // 1. Core Candle & Volume Data Update & Viewport Auto-Snap
  useEffect(() => {
    if (!chartRef.current || !bars || bars.length === 0) return;

    try {
      const n = bars.length;
      const currentKey = `${currentSymbol}_${currentTimeframe}`;
      const isSymbolOrTfChanged = prevKeyRef.current !== currentKey;
      prevKeyRef.current = currentKey;

      if (isSymbolOrTfChanged) {
        pendingSnapCountRef.current = 2;
      }

      const isReplayStepForward = isReplayMode && (n === prevBarsLengthRef.current + 1);
      const isSameDatasetBase = !isSymbolOrTfChanged && (
        bars[0]?.time === prevBarsFirstTimeRef.current &&
        (n === prevBarsLengthRef.current || n === prevBarsLengthRef.current + 1)
      );
      const isTimeGap = !isReplayMode && prevBarsLastTimeRef.current !== null && Math.abs((bars[n - 1]?.time || 0) - prevBarsLastTimeRef.current) > 300;
      const prevLen = prevBarsLengthRef.current || 0;
      prevBarsLengthRef.current = n;
      prevBarsFirstTimeRef.current = bars[0]?.time;
      prevBarsLastTimeRef.current = bars[n - 1]?.time;
      prevBarsRef.current = bars;

      const { precision, minMove } = getPrecision(currentSymbol);
      candleSeriesRef.current?.applyOptions({
        priceFormat: { type: "price", precision, minMove },
      });

      const lastBar = bars[n - 1];
      const latestCandle = {
        time: lastBar.time,
        open: lastBar.open,
        high: lastBar.high,
        low: lastBar.low,
        close: lastBar.close,
      };
      const latestVolume = {
        time: lastBar.time,
        value: lastBar.volume || 10,
        color: lastBar.close >= lastBar.open ? "rgba(16, 185, 129, 0.25)" : "rgba(239, 68, 68, 0.25)",
      };

      if ((isSameDatasetBase && !isTimeGap && pendingSnapCountRef.current === 0) || isReplayStepForward) {
        // Smooth 60 FPS in-place update or single candle append (Preserves user's scroll & zoom position)
        candleSeriesRef.current?.update(latestCandle);
        volumeSeriesRef.current?.update(latestVolume);

        // In Replay Mode: Auto-scroll 1 bar right if user is tracking at right edge
        if (isReplayMode && n > prevLen) {
          const currentRange = chartRef.current?.timeScale().getVisibleLogicalRange();
          const isAtEdge = !currentRange || currentRange.to >= (n - 15);
          if (isAtEdge && currentRange) {
            const shift = n - prevLen;
            if (shift > 0 && shift <= 5) {
              requestAnimationFrame(() => {
                try {
                  chartRef.current?.timeScale().setVisibleLogicalRange({
                    from: currentRange.from + shift,
                    to: currentRange.to + shift,
                  });
                  rsiChartRef.current?.timeScale().setVisibleLogicalRange({
                    from: currentRange.from + shift,
                    to: currentRange.to + shift,
                  });
                } catch (e) {}
              });
            }
          }
        }
      } else {
        // Full Dataset Reload (Symbol switch, Timeframe switch, or initial load / count change)
        const formattedCandles = [];
        const formattedVolume = [];
        let lastTime = -1;

        for (let i = 0; i < bars.length; i++) {
          const b = bars[i];
          if (!b || typeof b.time !== "number" || !isFinite(b.close) || b.time <= lastTime) {
            continue;
          }
          lastTime = b.time;
          formattedCandles.push({
            time: b.time,
            open: b.open,
            high: b.high,
            low: b.low,
            close: b.close,
          });
          formattedVolume.push({
            time: b.time,
            value: b.volume || 10,
            color: b.close >= b.open ? "rgba(16, 185, 129, 0.25)" : "rgba(239, 68, 68, 0.25)",
          });
        }

        const totalBars = formattedCandles.length;
        candleSeriesRef.current?.setData(formattedCandles);
        volumeSeriesRef.current?.setData(formattedVolume);
        prevMarkersKeyRef.current = "";

        const isMajorLengthChange = Math.abs(totalBars - prevLen) > 5;
        const mustSnap = pendingSnapCountRef.current > 0 || isSymbolOrTfChanged || isMajorLengthChange || isTimeGap || isInitialLoadRef.current;
        if (pendingSnapCountRef.current > 0) {
          pendingSnapCountRef.current -= 1;
        }
        isInitialLoadRef.current = false;

        const currentRange = chartRef.current?.timeScale().getVisibleLogicalRange();
        const isUserAtRightEdge = !currentRange || currentRange.to >= (totalBars - 15);

        if ((mustSnap || isUserAtRightEdge) && !isReplayMode && totalBars > 0) {
          const span = Math.min(55, totalBars);
          const rightOffsetBars = 10;
          const range = {
            from: Math.max(0, totalBars - span),
            to: totalBars + rightOffsetBars,
          };
          requestAnimationFrame(() => {
            try {
              chartRef.current?.priceScale("right")?.applyOptions({ autoScale: true });
              chartRef.current?.timeScale().applyOptions({ rightOffset: 10, barSpacing: 18 });
              chartRef.current?.timeScale().setVisibleLogicalRange(range);
              if (rsiChartRef.current) {
                rsiChartRef.current.priceScale("right")?.applyOptions({ autoScale: true });
                rsiChartRef.current.timeScale().applyOptions({ rightOffset: 10, barSpacing: 18 });
                rsiChartRef.current.timeScale().setVisibleLogicalRange(range);
              }
            } catch (err) {}
          });
        } else if (currentRange && !mustSnap) {
          requestAnimationFrame(() => {
            try {
              chartRef.current?.timeScale().setVisibleLogicalRange(currentRange);
              if (rsiChartRef.current) {
                rsiChartRef.current.timeScale().setVisibleLogicalRange(currentRange);
              }
            } catch (err) {}
          });
        }
      }

      if (isReplayMode && targetSeekLogicalRef.current !== null) {
        const targetIdx = targetSeekLogicalRef.current;
        targetSeekLogicalRef.current = null;
        const timeScale = chartRef.current?.timeScale();
        if (timeScale) {
          const currentRange = timeScale.getVisibleLogicalRange();
          const span = currentRange ? Math.max(25, Math.min(150, currentRange.to - currentRange.from)) : 75;
          const rightOffsetBars = 12;
          const newFrom = Math.max(0, targetIdx - span + rightOffsetBars);
          const newTo = targetIdx + rightOffsetBars;
          requestAnimationFrame(() => {
            try {
              timeScale.setVisibleLogicalRange({ from: newFrom, to: newTo });
              rsiChartRef.current?.timeScale().setVisibleLogicalRange({ from: newFrom, to: newTo });
            } catch (err) {}
          });
        }
      }
    } catch (err) {
      console.warn("Candle series update error:", err);
    }
  }, [bars, currentSymbol, currentTimeframe, isReplayMode]);

  // 2. Technical Indicators (EMA 9/21/50/200, Bollinger Bands, RSI)
  useEffect(() => {
    if (!chartRef.current) return;
    try {
      if (indicators) {
        ema9SeriesRef.current?.setData(showEMA ? indicators.ema9 || [] : []);
        ema21SeriesRef.current?.setData(showEMA ? indicators.ema21 || [] : []);
        ema50SeriesRef.current?.setData(showEMA ? indicators.ema50 || [] : []);
        ema200SeriesRef.current?.setData(showEMA ? indicators.ema200 || [] : []);

        const isBBUpperVis = isBBActive && (indicatorStyles.bb?.upper?.visible !== false);
        const isBBLowerVis = isBBActive && (indicatorStyles.bb?.lower?.visible !== false);
        bbUpperRef.current?.setData(isBBUpperVis ? indicators.bollinger?.upper || [] : []);
        bbLowerRef.current?.setData(isBBLowerVis ? indicators.bollinger?.lower || [] : []);

        if (rsiSeriesRef.current) {
          rsiSeriesRef.current.setData(showRSI ? indicators.rsi || [] : []);
        }
      } else {
        ema9SeriesRef.current?.setData([]);
        ema21SeriesRef.current?.setData([]);
        ema50SeriesRef.current?.setData([]);
        ema200SeriesRef.current?.setData([]);
        bbUpperRef.current?.setData([]);
        bbLowerRef.current?.setData([]);
        rsiSeriesRef.current?.setData([]);
      }
    } catch (err) {
      console.warn("Indicators update error:", err);
    }
  }, [indicators, showEMA, isBBActive, showRSI, indicatorStyles.bb]);

  // 3. Ichimoku Cloud Data
  useEffect(() => {
    if (!chartRef.current) return;
    try {
      if (ichimokuData && isIchimokuActive) {
        const ichiSt = indicatorStyles.ichimoku || {};
        ichiTenkanRef.current?.setData(ichiSt.tenkan?.visible !== false ? ichimokuData.tenkan || [] : []);
        ichiKijunRef.current?.setData(ichiSt.kijun?.visible !== false ? ichimokuData.kijun || [] : []);
        ichiChikouRef.current?.setData(ichiSt.chikou?.visible !== false ? ichimokuData.chikou || [] : []);
        ichiSpanARef.current?.setData(ichiSt.spanA?.visible !== false ? ichimokuData.spanA || [] : []);
        ichiSpanBRef.current?.setData(ichiSt.spanB?.visible !== false ? ichimokuData.spanB || [] : []);
      } else {
        ichiTenkanRef.current?.setData([]);
        ichiKijunRef.current?.setData([]);
        ichiChikouRef.current?.setData([]);
        ichiSpanARef.current?.setData([]);
        ichiSpanBRef.current?.setData([]);
      }
    } catch (err) {
      console.warn("Ichimoku update error:", err);
    }
  }, [ichimokuData, isIchimokuActive, indicatorStyles.ichimoku]);

  // 4. Multi EMA Ribbon Data
  useEffect(() => {
    if (!chartRef.current) return;
    try {
      if (multiEmaData && isMultiEmaActive) {
        const mSt = indicatorStyles.multiEma || {};
        multiEma1Ref.current?.setData(mSt.ema1?.visible !== false ? multiEmaData.ema1 || [] : []);
        multiEma2Ref.current?.setData(mSt.ema2?.visible !== false ? multiEmaData.ema2 || [] : []);
        multiEma3Ref.current?.setData(mSt.ema3?.visible !== false ? multiEmaData.ema3 || [] : []);
        multiEma4Ref.current?.setData(mSt.ema4?.visible !== false ? multiEmaData.ema4 || [] : []);
      } else {
        multiEma1Ref.current?.setData([]);
        multiEma2Ref.current?.setData([]);
        multiEma3Ref.current?.setData([]);
        multiEma4Ref.current?.setData([]);
      }
    } catch (err) {
      console.warn("Multi EMA update error:", err);
    }
  }, [multiEmaData, isMultiEmaActive, indicatorStyles.multiEma]);

  // 4B. Bob Volman 20 EMA Native Series Data (GPU-accelerated native series)
  useEffect(() => {
    if (!chartRef.current || !bobVolmanEmaRef.current) return;
    try {
      const isBVActive = isBobVolmanActive && Boolean(strategyParams?.volmanShowEMA);
      const bvSt = indicatorStyles.bobVolman || {};
      bobVolmanEmaRef.current.applyOptions({
        color: bvSt.emaLine?.color || "#06b6d4",
        lineWidth: bvSt.emaLine?.width || 2,
      });
      if (isBVActive && bobVolmanData?.ema20 && bobVolmanData.ema20.length >= 2) {
        bobVolmanEmaRef.current.setData(bobVolmanData.ema20);
      } else {
        bobVolmanEmaRef.current.setData([]);
      }
    } catch (err) {
      console.warn("Bob Volman EMA series update error:", err);
    }
  }, [bobVolmanData, isBobVolmanActive, strategyParams?.volmanShowEMA, indicatorStyles.bobVolman]);

  // 5. Signals Markers (Arrows on candles)
  useEffect(() => {
    if (!markersRef.current) return;
    try {
      let activeMarkers = [];
      if (strategyMode === "scalping_pro" || isScalpingProActive) {
        if (scalpingProData?.chart_markers || scalpingProData?.chartMarkers) {
          activeMarkers = [...(scalpingProData.chart_markers || scalpingProData.chartMarkers)];
        } else if (scalpingPro?.chart_markers || scalpingPro?.chartMarkers) {
          activeMarkers = [...(scalpingPro.chart_markers || scalpingPro.chartMarkers)];
        }
      } else if (strategyMode === "liquidity_sweep" || isLiquiditySweepActive) {
        if (liquiditySweepData?.chart_markers || liquiditySweepData?.chartMarkers) {
          activeMarkers = [...(liquiditySweepData.chart_markers || liquiditySweepData.chartMarkers)];
        } else if (liquiditySweep?.chart_markers || liquiditySweep?.chartMarkers) {
          activeMarkers = [...(liquiditySweep.chart_markers || liquiditySweep.chartMarkers)];
        }
      } else if (strategyMode === "orb_session" || isOrbSessionActive) {
        if (orbSessionData?.chart_markers || orbSessionData?.chartMarkers) {
          activeMarkers = [...(orbSessionData.chart_markers || orbSessionData.chartMarkers)];
        }
      } else if (strategyMode === "bob_volman" || isBobVolmanActive) {
        if (bobVolmanData?.chart_markers || bobVolmanData?.chartMarkers) {
          activeMarkers = [...(bobVolmanData.chart_markers || bobVolmanData.chartMarkers)];
        }
      } else if (strategyMode === "hma" || isHMABreakoutActive) {
        if (hmaSystemData?.chart_markers || hmaSystemData?.chartMarkers) {
          activeMarkers = [...(hmaSystemData.chart_markers || hmaSystemData.chartMarkers)];
        }
      } else if (strategyMode === "smc" || isMSBDynamicActive) {
        if (smcData?.signals?.chart_markers) {
          activeMarkers = [...smcData.signals.chart_markers];
        } else if (smc?.signals?.chart_markers) {
          activeMarkers = [...smc.signals.chart_markers];
        }
      } else {
        if (isMatrixProActive && signals?.chart_markers) {
          activeMarkers = [...signals.chart_markers];
        }
      }
      if (pineResult?.markers && pineResult.markers.length > 0) {
        activeMarkers = [...activeMarkers, ...pineResult.markers];
      }

      const uniqueMarkersMap = new Map();
      activeMarkers.forEach((m) => {
        const key = `${m.time}_${m.position || m.shape || ""}`;
        uniqueMarkersMap.set(key, m);
      });
      const dedupedMarkers = Array.from(uniqueMarkersMap.values());
      dedupedMarkers.sort((a, b) => a.time - b.time);

      const lastM = dedupedMarkers[dedupedMarkers.length - 1];
      const markersKey = dedupedMarkers.length > 0 ? `${dedupedMarkers.length}_${lastM?.time}_${lastM?.text}_${lastM?.color}` : "none";
      if (prevMarkersKeyRef.current !== markersKey) {
        prevMarkersKeyRef.current = markersKey;
        markersRef.current.setMarkers(dedupedMarkers);
      }
    } catch (err) {
      console.warn("Markers update error:", err);
    }
  }, [strategyMode, isScalpingProActive, scalpingProData, scalpingPro, isLiquiditySweepActive, liquiditySweepData, liquiditySweep, isOrbSessionActive, orbSessionData, isBobVolmanActive, bobVolmanData, isHMABreakoutActive, hmaSystemData, isMSBDynamicActive, smcData, smc, isMatrixProActive, signals, pineResult, bars]);

  // 6. Visual Price Lines (ENTRY, TP, SL on Right Axis)
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    let activeSig = null;
    let shouldShowLines = false;

    if ((strategyMode === "scalping_pro" || isScalpingProActive) && (scalpingProData?.latest_signal || scalpingProData?.latestSignal)) {
      activeSig = scalpingProData.latest_signal || scalpingProData.latestSignal;
      const status = (activeSig.status || "").toUpperCase();
      shouldShowLines = ["OPEN", "PENDING", "RUNNING", "FILLED", "PENDING_LIMIT"].includes(status);
    } else if ((strategyMode === "liquidity_sweep" || isLiquiditySweepActive) && (liquiditySweepData?.latest_signal || liquiditySweepData?.latestSignal)) {
      activeSig = liquiditySweepData.latest_signal || liquiditySweepData.latestSignal;
      const status = (activeSig.status || "").toUpperCase();
      shouldShowLines = ["OPEN", "PENDING", "RUNNING", "FILLED", "PENDING_LIMIT"].includes(status);
    } else if ((strategyMode === "orb_session" || isOrbSessionActive) && (orbSessionData?.latest_signal || orbSessionData?.latestSignal)) {
      activeSig = orbSessionData.latest_signal || orbSessionData.latestSignal;
      const status = (activeSig.status || "").toUpperCase();
      shouldShowLines = ["OPEN", "PENDING", "RUNNING", "FILLED", "PENDING_LIMIT"].includes(status);
    } else if ((strategyMode === "bob_volman" || isBobVolmanActive) && (bobVolmanData?.latest_signal || bobVolmanData?.latestSignal)) {
      activeSig = bobVolmanData.latest_signal || bobVolmanData.latestSignal;
      const status = (activeSig.status || "").toUpperCase();
      shouldShowLines = ["OPEN", "PENDING", "RUNNING", "FILLED", "PENDING_LIMIT"].includes(status);
    } else if ((strategyMode === "hma" || isHMABreakoutActive) && (hmaSystemData?.latest_signal || hmaSystemData?.latestSignal)) {
      activeSig = hmaSystemData.latest_signal || hmaSystemData.latestSignal;
      const status = (activeSig.status || "").toUpperCase();
      shouldShowLines = ["OPEN", "PENDING", "RUNNING", "FILLED", "PENDING_LIMIT"].includes(status);
    } else if ((strategyMode === "smc" || isMSBDynamicActive) && (smcData?.signals?.latest_signal || smc?.signals?.latest_signal || smcData?.latest_signal || smc?.latest_signal)) {
      activeSig = smcData?.signals?.latest_signal || smc?.signals?.latest_signal || smcData?.latest_signal || smc?.latest_signal;
      const status = (activeSig.status || "").toUpperCase();
      shouldShowLines = ["OPEN", "PENDING", "RUNNING", "FILLED", "PENDING_LIMIT"].includes(status);
    } else if (isMatrixProActive && signals?.latest_signal) {
      activeSig = signals.latest_signal;
      const status = (activeSig.status || "").toUpperCase();
      shouldShowLines = showTPSL && ["OPEN", "PENDING", "RUNNING", "FILLED", "PENDING_LIMIT"].includes(status);
    }

    if (activeSig && shouldShowLines) {
      const sigKey = `${activeSig.id || activeSig.time}_${activeSig.price}_${activeSig.tp1}_${activeSig.sl}`;
      if (prevSignalKeyRef.current !== sigKey) {
        prevSignalKeyRef.current = sigKey;

        const removeLine = (ref) => {
          if (ref.current && candleSeriesRef.current) {
            try { candleSeriesRef.current.removePriceLine(ref.current); } catch (e) {}
            ref.current = null;
          }
        };
        removeLine(entryLineRef);
        removeLine(tp1LineRef);
        removeLine(tp2LineRef);
        removeLine(slLineRef);

        try {
          if (Number.isFinite(activeSig.price)) {
            entryLineRef.current = candleSeriesRef.current.createPriceLine({
              price: activeSig.price,
              color: activeSig.is_revert ? "#f97316" : "#38bdf8",
              lineWidth: 1.5,
              lineStyle: LineStyle.Solid,
              axisLabelVisible: true,
              title: activeSig.is_revert ? `ENTRY (REVERT ${activeSig.action})` : `ENTRY (${activeSig.action})`,
            });
          }

          const isSingleTpStrategy = isLiquiditySweepActive || strategyMode === "liquidity_sweep" || isScalpingProActive || strategyMode === "scalping_pro" || isHMABreakoutActive || strategyMode === "hma" || isBobVolmanActive || strategyMode === "bob_volman" || isOrbSessionActive || strategyMode === "orb_session";
          const tpPrice = (isLiquiditySweepActive || strategyMode === "liquidity_sweep" || isScalpingProActive || strategyMode === "scalping_pro") ? (activeSig.tp || activeSig.tp2 || activeSig.tp1) : (activeSig.tp1 || activeSig.tp);

          if (Number.isFinite(tpPrice)) {
            tp1LineRef.current = candleSeriesRef.current.createPriceLine({
              price: tpPrice,
              color: "#10b981",
              lineWidth: 1.5,
              lineStyle: LineStyle.Dashed,
              axisLabelVisible: true,
              title: activeSig.is_revert 
                ? `TP (REVERT ${activeSig.rr_ratio || ''})` 
                : (isSingleTpStrategy ? "TP (Target)" : "TP1 (Target)"),
            });
          }

          if (Number.isFinite(activeSig.tp2) && !activeSig.is_revert && !isSingleTpStrategy) {
            tp2LineRef.current = candleSeriesRef.current.createPriceLine({
              price: activeSig.tp2,
              color: "#059669",
              lineWidth: 1,
              lineStyle: LineStyle.Dotted,
              axisLabelVisible: true,
              title: "TP2 (Max)",
            });
          }

          if (Number.isFinite(activeSig.sl)) {
            slLineRef.current = candleSeriesRef.current.createPriceLine({
              price: activeSig.sl,
              color: "#ef4444",
              lineWidth: 1.5,
              lineStyle: LineStyle.Dashed,
              axisLabelVisible: true,
              title: activeSig.is_revert ? "SL (REVERT)" : "SL (Stop Loss)",
            });
          }
        } catch (e) {
          console.warn("Price lines error:", e);
        }
      }
    } else {
      const removeLine = (ref) => {
        if (ref.current && candleSeriesRef.current) {
          try { candleSeriesRef.current.removePriceLine(ref.current); } catch (e) {}
          ref.current = null;
        }
      };
      removeLine(entryLineRef);
      removeLine(tp1LineRef);
      removeLine(tp2LineRef);
      removeLine(slLineRef);
      prevSignalKeyRef.current = null;
    }
  }, [strategyMode, isScalpingProActive, scalpingProData, scalpingPro, isLiquiditySweepActive, liquiditySweepData, liquiditySweep, isOrbSessionActive, orbSessionData, isBobVolmanActive, bobVolmanData, isHMABreakoutActive, hmaSystemData, isMSBDynamicActive, smcData, smc, isMatrixProActive, signals, showTPSL]);

  // Master Canvas Overlay Render Loop Trigger
  useEffect(() => {
    requestAnimationFrame(() => {
      renderCanvasOverlay();
    });
  }, [renderCanvasOverlay]);

  // Real-time tick & WebSocket bar update (smooth direct candle update without full re-render)
  useEffect(() => {
    if (isReplayMode) return;
    if (!candleSeriesRef.current) return;

    if (
      bars &&
      bars.length > 0 &&
      lastWsBar &&
      lastWsBar.symbol === currentSymbol &&
      lastWsBar.timeframe === currentTimeframe
    ) {
      try {
        candleSeriesRef.current.update({
          time: lastWsBar.time,
          open: lastWsBar.open,
          high: lastWsBar.high,
          low: lastWsBar.low,
          close: lastWsBar.close,
        });
        if (volumeSeriesRef.current && lastWsBar.volume !== undefined) {
          volumeSeriesRef.current.update({
            time: lastWsBar.time,
            value: lastWsBar.volume || 10,
            color: lastWsBar.close >= lastWsBar.open ? "rgba(16, 185, 129, 0.25)" : "rgba(239, 68, 68, 0.25)",
          });
        }
      } catch (e) {
        console.warn("lastWsBar update error:", e);
      }
      return;
    }

    if (tick && tick.symbol === currentSymbol && bars && bars.length > 0) {
      const lastBar = bars[bars.length - 1];
      if (lastBar) {
        const price = tick.bid || tick.ask || tick.last || tick.close || lastBar.close;
        try {
          candleSeriesRef.current.update({
            time: lastBar.time,
            open: lastBar.open,
            high: Math.max(lastBar.high, price),
            low: Math.min(lastBar.low, price),
            close: price,
          });
        } catch (e) {
          console.warn("tick update error:", e);
        }
      }
    }
  }, [tick, lastWsBar, currentSymbol, currentTimeframe, isReplayMode, bars]);

  const fitContent = useCallback(() => {
    try {
      const totalBars = bars?.length || 0;
      if (chartRef.current && totalBars > 0) {
        const span = Math.min(55, totalBars);
        const rightOffsetBars = 10;
        const newFrom = Math.max(0, totalBars - span);
        const newTo = totalBars + rightOffsetBars;

        chartRef.current.priceScale("right")?.applyOptions({ autoScale: true });
        chartRef.current.timeScale()?.applyOptions({ rightOffset: 10, barSpacing: 18 });
        chartRef.current.timeScale()?.setVisibleLogicalRange({ from: newFrom, to: newTo });
      }
      if (candleSeriesRef.current) {
        candleSeriesRef.current.priceScale()?.applyOptions({ autoScale: true });
      }
      if (rsiChartRef.current && totalBars > 0) {
        const span = Math.min(55, totalBars);
        const rightOffsetBars = 10;
        const newFrom = Math.max(0, totalBars - span);
        const newTo = totalBars + rightOffsetBars;
        rsiChartRef.current.priceScale("right")?.applyOptions({ autoScale: true });
        rsiChartRef.current.timeScale()?.applyOptions({ rightOffset: 10, barSpacing: 18 });
        rsiChartRef.current.timeScale()?.setVisibleLogicalRange({ from: newFrom, to: newTo });
      }
    } catch (e) {
      console.warn("fitContent error:", e);
    }
    setTimeout(() => renderCanvasOverlay(), 50);
  }, [bars, renderCanvasOverlay]);

  const handleToggleFullscreen = () => {
    const container = mainWrapperRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen().catch(() => {});
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      setTimeout(() => {
        if (chartRef.current && chartContainerRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
          });
        }
        renderCanvasOverlay();
      }, 100);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, [renderCanvasOverlay]);

  const selectedDrawing = drawings.find((d) => d.id === selectedDrawingId);

  return (
    <div
      ref={mainWrapperRef}
      className={`relative bg-slate-900/60 border border-slate-800/80 rounded-xl overflow-hidden flex flex-col h-full min-h-[440px] sm:min-h-[580px] lg:min-h-[640px] shadow-xl ${
        isFullscreen ? "!fixed !inset-0 !z-50 !bg-slate-950 !rounded-none !border-none !w-screen !h-screen" : ""
      } ${isSelectingBar ? "cursor-crosshair ring-1 ring-cyan-400" : ""}`}
    >
      {/* Top Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 bg-slate-900/90 border-b border-slate-800 text-xs overflow-x-auto scrollbar-none">
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="flex items-center gap-1 font-bold text-slate-200 text-xs sm:text-sm">
            <span>{currentSymbol}</span>
            <span className="text-slate-400 font-medium text-xs">({currentTimeframe})</span>
          </div>

          <div className="h-4 w-px bg-slate-800" />

          {/* Modular Indicators Button */}
          <button
            onClick={handleOpenLibrary}
            className="px-2.5 sm:px-3 py-1.5 rounded-xl flex items-center gap-1.5 font-semibold bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 transition cursor-pointer shadow-sm text-xs"
            title="Thư viện chỉ báo & Hệ thống chiến lược"
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Chỉ báo ({activeIndicators.length})</span>
          </button>

          {/* Alert Modal Button */}
          <button
            onClick={onOpenAlerts}
            className="relative px-2.5 sm:px-3 py-1.5 rounded-xl flex items-center gap-1.5 font-semibold bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 transition cursor-pointer shadow-sm text-xs"
            title="Cài đặt và quản lý cảnh báo giá / chiến lược"
          >
            <Bell className="w-3.5 h-3.5 text-amber-400" />
            <span>Cảnh báo</span>
            {activeAlertsCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-rose-500 text-white font-bold text-[9px] flex items-center justify-center shadow-sm">
                {activeAlertsCount}
              </span>
            )}
          </button>

          {/* Real-time Auto-Save & Cloud Sync Badge */}
          <div 
            className={`hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-[11px] font-mono select-none transition ${
              syncStatus === 'saving'
                ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
            }`}
            title="Hệ thống tự động lưu và đồng bộ tài khoản mọi cài đặt, chỉ báo và bản vẽ"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${syncStatus === 'saving' ? "bg-amber-400 animate-spin" : "bg-emerald-400 animate-pulse"}`} />
            <span>{syncStatus === 'saving' ? "Đang lưu..." : "Đã đồng bộ"}</span>
          </div>
        </div>

        {/* Right tools */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Bar Replay Trigger Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isReplayMode) {
                onExitReplay?.();
              } else {
                onStartReplay?.();
              }
            }}
            className={`px-3 py-1.5 rounded-xl flex items-center gap-1.5 font-semibold transition cursor-pointer text-xs ${
              isReplayMode
                ? "bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20"
                : "bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-cyan-500/30"
            }`}
            title="Mở chế độ Tua nến & Phát lại (TradingView Replay)"
          >
            <FastForward className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{isReplayMode ? "Đang Replay ⏪" : "Bar Replay ⏪"}</span>
            <span className="sm:hidden">Replay</span>
          </button>

          {/* Fullscreen Toggle Button */}
          <button
            onClick={handleToggleFullscreen}
            title={isFullscreen ? "Thu nhỏ / Thoát toàn màn hình (Esc)" : "Phóng to toàn màn hình biểu đồ (Fullscreen)"}
            className={`p-1.5 rounded-lg transition cursor-pointer flex items-center justify-center ${
              isFullscreen
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/40 hover:bg-amber-500/30 shadow-sm"
                : "hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-transparent"
            }`}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Main Area: Left Drawing Dock + Chart Canvas */}
      <div className="relative flex-1 flex w-full min-h-[380px] sm:min-h-[480px]">
        {/* Left Drawing Tools Toolbar */}
        <DrawingToolbar
          activeTool={activeTool}
          onSelectTool={(tool) => {
            setActiveTool(tool);
            setSelectedDrawingId(null);
          }}
          onClearAll={handleClearAllDrawings}
          drawingCount={drawings.length}
          activeUserId={activeUserId}
          userProfiles={userProfiles}
          onSwitchUser={handleSelectUser}
          onOpenUserModal={() => setIsUserModalOpen(true)}
          drawColor={drawColor}
          onChangeDrawColor={setDrawColor}
        />

        {/* Chart Canvas Area */}
        <div className="relative flex-1 h-full" onMouseMove={handleContainerMouseMove} onDoubleClick={fitContent}>
          <div ref={chartContainerRef} className="absolute inset-0 w-full h-full" />

          {/* Interactive Drawing & Kumo Cloud Canvas Overlay */}
          <canvas
            ref={overlayCanvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            className={`absolute inset-0 w-full h-full z-10 ${
              activeTool !== "cursor" ? "pointer-events-auto" : "pointer-events-none"
            }`}
          />

          {/* TradingView-style Floating Context Action Bar */}
          <DrawingActionBar
            selectedDrawing={selectedDrawing}
            position={actionBarPos}
            onOpenSettings={(d) => {
              if (d.type === "fibonacci") setFibModalDrawing(d);
            }}
            onUpdateDrawing={handleUpdateSelectedDrawing}
            onDeleteDrawing={handleDeleteSelectedDrawing}
            onDuplicateDrawing={handleDuplicateDrawing}
            onDeselect={() => setSelectedDrawingId(null)}
          />

          {/* TradingView-style Top-Left Indicator Legend */}
          <div className="absolute top-2.5 sm:top-3 left-2 sm:left-4 z-20 flex flex-col gap-1 text-[10px] sm:text-[11px] font-mono pointer-events-auto max-w-[calc(100vw-3.5rem)]">
            <div className="space-y-1">
              {displayedIndicators.map((indId) => {
                const def = AVAILABLE_INDICATORS.find((i) => i.id === indId);
                if (!def) return null;
                const isHidden = hiddenIndicators.includes(indId);
                const isLocked = def.isSystem && !isBasic;

                return (
                  <div
                    key={indId}
                    className={`group flex items-center gap-1.5 sm:gap-2 bg-slate-950/85 hover:bg-slate-950 px-2.5 py-1 rounded-xl border backdrop-blur-md transition text-[10px] sm:text-[11px] shadow-md w-fit max-w-full ${
                      isLocked ? "border-amber-500/40 opacity-95" : "border-slate-800/60"
                    }`}
                  >
                    <span
                      onClick={isLocked ? () => promptTierGate(def.name, "BASIC") : undefined}
                      className={`font-semibold flex items-center gap-1 sm:gap-1.5 truncate ${
                        isLocked
                          ? "text-amber-400 cursor-pointer hover:underline"
                          : isHidden
                          ? "text-slate-500 line-through"
                          : def.isSystem
                          ? "text-amber-400"
                          : "text-cyan-400"
                      }`}
                    >
                      {isLocked && <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400 shrink-0" />}
                      <span className="hidden sm:inline truncate">{def.name}</span>
                      <span className="sm:hidden truncate">{def.shortName || def.name}</span>
                      {indId === "hma_indicator" && hmaData?.latest?.value !== undefined && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: hmaData.latest.isBull ? (indicatorStyles.hma?.bullColor || "#10b981") : (indicatorStyles.hma?.bearColor || "#ef4444") }}
                        >
                          {hmaData.latest.value} ({hmaPeriod})
                        </span>
                      )}
                      {indId === "hma_breakout_system" && !isHidden && Boolean(hmaSystemData?.latest_signal || hmaSystemData?.latestSignal) && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: (hmaSystemData.latest_signal || hmaSystemData.latestSignal)?.action === "BUY" ? "#10b981" : "#ef4444" }}
                        >
                          {(hmaSystemData.latest_signal || hmaSystemData.latestSignal)?.action} @ {(hmaSystemData.latest_signal || hmaSystemData.latestSignal)?.price}
                        </span>
                      )}
                      {indId === "msb_dynamic" && !isHidden && Boolean(smcData?.latest_signal || smcData?.latestSignal || smc?.latest_signal || smc?.latestSignal || smcData?.signals?.latest_signal || smc?.signals?.latest_signal) && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: (smcData?.latest_signal || smcData?.latestSignal || smc?.latest_signal || smc?.latestSignal || smcData?.signals?.latest_signal || smc?.signals?.latest_signal)?.action === "BUY" ? "#10b981" : "#ef4444" }}
                        >
                          {(smcData?.latest_signal || smcData?.latestSignal || smc?.latest_signal || smc?.latestSignal || smcData?.signals?.latest_signal || smc?.signals?.latest_signal)?.action} @ {(smcData?.latest_signal || smcData?.latestSignal || smc?.latest_signal || smc?.latestSignal || smcData?.signals?.latest_signal || smc?.signals?.latest_signal)?.price}
                        </span>
                      )}
                      {indId === "bob_volman_system" && !isHidden && Boolean(bobVolmanData?.latest_signal || bobVolmanData?.latestSignal || bobVolman?.latest_signal || bobVolman?.latestSignal) && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: (bobVolmanData?.latest_signal || bobVolmanData?.latestSignal || bobVolman?.latest_signal || bobVolman?.latestSignal)?.action === "BUY" ? "#10b981" : "#ef4444" }}
                        >
                          {(bobVolmanData?.latest_signal || bobVolmanData?.latestSignal || bobVolman?.latest_signal || bobVolman?.latestSignal)?.action} @ {(bobVolmanData?.latest_signal || bobVolmanData?.latestSignal || bobVolman?.latest_signal || bobVolman?.latestSignal)?.price}
                        </span>
                      )}
                      {indId === "orb_session_system" && !isHidden && Boolean(orbSessionData?.latest_signal || orbSessionData?.latestSignal || orbSession?.latest_signal || orbSession?.latestSignal) && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: (orbSessionData?.latest_signal || orbSessionData?.latestSignal || orbSession?.latest_signal || orbSession?.latestSignal)?.action === "BUY" ? "#10b981" : "#ef4444" }}
                        >
                          {(orbSessionData?.latest_signal || orbSessionData?.latestSignal || orbSession?.latest_signal || orbSession?.latestSignal)?.action} @ {(orbSessionData?.latest_signal || orbSessionData?.latestSignal || orbSession?.latest_signal || orbSession?.latestSignal)?.price}
                        </span>
                      )}
                      {indId === "scalping_pro_system" && !isHidden && Boolean(scalpingProData?.latest_signal || scalpingProData?.latestSignal) && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: (scalpingProData?.latest_signal || scalpingProData?.latestSignal)?.action === "BUY" ? "#089981" : "#f23645" }}
                        >
                          {(scalpingProData?.latest_signal || scalpingProData?.latestSignal || scalpingPro?.latest_signal || scalpingPro?.latestSignal)?.action} @ {(scalpingProData?.latest_signal || scalpingProData?.latestSignal || scalpingPro?.latest_signal || scalpingPro?.latestSignal)?.price}
                        </span>
                      )}
                      {indId === "liquidity_sweep_pro" && !isHidden && Boolean(liquiditySweepData?.latest_signal || liquiditySweepData?.latestSignal || liquiditySweep?.latest_signal || liquiditySweep?.latestSignal) && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: (liquiditySweepData?.latest_signal || liquiditySweepData?.latestSignal || liquiditySweep?.latest_signal || liquiditySweep?.latestSignal)?.action === "BUY" ? "#10b981" : "#ef4444" }}
                        >
                          {(liquiditySweepData?.latest_signal || liquiditySweepData?.latestSignal || liquiditySweep?.latest_signal || liquiditySweep?.latestSignal)?.action} @ {(liquiditySweepData?.latest_signal || liquiditySweepData?.latestSignal || liquiditySweep?.latest_signal || liquiditySweep?.latestSignal)?.price}
                        </span>
                      )}
                      {indId === "matrix_pro" && !isHidden && Boolean(signals?.latest_signal || signals?.latestSignal) && (
                        <span 
                          className="font-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.2 sm:py-0.5 rounded bg-slate-900/80 border border-slate-700/50 ml-0.5 sm:ml-1 shrink-0"
                          style={{ color: (signals.latest_signal || signals.latestSignal)?.action === "BUY" ? "#10b981" : "#ef4444" }}
                        >
                          {(signals.latest_signal || signals.latestSignal)?.action} @ {(signals.latest_signal || signals.latestSignal)?.price}
                        </span>
                      )}
                      {isLocked && (
                        <span className="hidden sm:inline text-[9px] bg-amber-950/90 text-amber-300 px-1 py-0.2 rounded border border-amber-700/60 font-medium">
                          Đăng nhập Basic
                        </span>
                      )}
                    </span>

                    <div className="flex items-center gap-1 sm:gap-1.5 opacity-80 group-hover:opacity-100 transition shrink-0">
                      {!isLocked ? (
                        <>
                          <button
                            onClick={() => handleToggleHide(indId)}
                            className="p-0.5 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition cursor-pointer"
                            title={isHidden ? "Hiện hệ thống" : "Ẩn hệ thống"}
                          >
                            {isHidden ? <EyeOff className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-500" /> : <Eye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                          </button>

                          <button
                            onClick={() => handleOpenSettingsModal(indId)}
                            className="p-0.5 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition cursor-pointer"
                            title="Cài đặt thông số (Inputs, Style)"
                          >
                            <Settings className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => promptTierGate(def.name, "BASIC")}
                          className="px-1.5 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-[9px] sm:text-[10px] font-bold transition cursor-pointer flex items-center gap-1 border border-amber-500/30 shrink-0"
                        >
                          Mở Khóa
                        </button>
                      )}

                      <button
                        onClick={() => handleToggleIndicator(indId)}
                        className="p-0.5 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition cursor-pointer"
                        title="Xóa khỏi biểu đồ"
                      >
                        <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Floating Auto Fit Button (TradingView style) */}
          <button
            onClick={fitContent}
            className={`absolute z-20 px-2 sm:px-2.5 py-1.5 bg-slate-900/90 hover:bg-slate-800 text-cyan-400 hover:text-cyan-300 border border-slate-700/80 hover:border-cyan-500/40 rounded-lg text-[11px] font-semibold shadow-xl backdrop-blur-md transition flex items-center gap-1.5 cursor-pointer pointer-events-auto ${
              isReplayMode ? "bottom-14 sm:bottom-4 right-3 sm:right-20" : "bottom-4 right-3 sm:right-20"
            }`}
            title="Đưa nến và giá về trung tâm màn hình (Auto Scale) [Nhấp đúp chuột vào biểu đồ]"
          >
            <Target className="w-3.5 h-3.5 text-cyan-400" />
            <span className="hidden sm:inline">Về Nến Hiện Tại</span>
          </button>

          {/* Floating Bar Replay Bottom Dock */}
          <ReplayToolbar
            isReplayMode={isReplayMode}
            isSelectingBar={isSelectingBar}
            onToggleSelectBar={() => setIsSelectingBar(!isSelectingBar)}
            onExitReplay={onExitReplay}
            isPlaying={isPlaying}
            onTogglePlay={onTogglePlay}
            onStepForward={onStepForward}
            onResetReplay={onResetReplay}
            currentIndex={replayIndex}
            totalBars={totalBars}
            onSeek={onSeek}
            replaySpeed={replaySpeed}
            onChangeSpeed={onChangeSpeed}
          />
        </div>
      </div>

      {/* RSI Sub-Chart Panel */}
      {showRSI && (
        <div className="border-t border-slate-800 bg-slate-950/80 p-2 flex flex-col gap-1">
          <div className="flex items-center justify-between text-[11px] px-2 font-mono">
            <div className="flex items-center gap-2">
              <span className="font-bold text-fuchsia-400">RSI (14)</span>
              <span className="text-slate-400 font-bold">Value: {indicators?.latest?.rsi || 50}</span>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="text-rose-400">Quá Mua: 70</span>
              <span className="text-emerald-400">Quá Bán: 30</span>
            </div>
          </div>
          <div ref={rsiContainerRef} className="w-full h-[110px]" />
        </div>
      )}

      {/* Indicator Library Modal */}
      <IndicatorLibraryModal
        isOpen={isLibraryOpen}
        onClose={handleCloseLibrary}
        activeIndicators={activeIndicators}
        onToggleIndicator={handleToggleIndicator}
        onOpenSettings={(id) => {
          handleCloseLibrary();
          handleOpenSettingsModal(id);
        }}
      />

      {/* Indicator Settings Modal */}
      <IndicatorSettingsModal
        isOpen={!!activeSettingsModalId}
        onClose={handleCloseSettingsModal}
        indicatorId={activeSettingsModalId}
        strategyParams={strategyParams}
        onApplyParams={onApplyStrategy}
        indicatorStyles={indicatorStyles}
        onApplyStyles={handleApplyStyles}
        publicConfig={publicConfig}
      />

      {/* User Profile & Analysis Plan Modal */}
      <UserProfileModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        userProfiles={userProfiles}
        activeUserId={activeUserId}
        onSelectUser={handleSelectUser}
        onCreateProfile={handleCreateProfile}
        onDeleteProfile={handleDeleteProfile}
      />

      {/* Fibonacci Detailed Settings Modal (TradingView Style) */}
      <FibonacciSettingsModal
        isOpen={!!fibModalDrawing}
        onClose={() => setFibModalDrawing(null)}
        fibDrawing={fibModalDrawing}
        onSaveFibDrawing={handleUpdateSelectedDrawing}
      />
    </div>
  );
}